import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  playHandRaiseChime,
  playNotificationSound,
  startHoldMusic,
  startRingback,
  stopHoldMusic,
  stopRingback,
} from '../lib/sound';
import {
  applyBackground,
  NO_BACKGROUND,
  backgroundPreference,
  rememberBackground,
  type VideoBackground,
} from './blur';
import {
  applyNoiseFilter,
  applyRemoteNoiseFilter,
  noisePreference,
  rememberNoise,
  type NoiseLevel,
  type RemoteNoiseFilter,
} from './noise';
import {
  hideShareFrame,
  listMonitors,
  type Monitor,
  pickShareMonitor,
  probeMonitor,
  type ShareFrameState,
  showShareFrame,
  sourceSize,
} from '../lib/share-frame';
import {
  type Device,
  type DeviceKind,
  rememberDevice,
  tidyDevices,
  rememberedDevice,
  resolveDevice,
} from './devices';
import { clamp, loadVolumes, rememberVolume } from './participant-volume';
import { describeMediaError, sourceFromKind } from './media-errors';
import { ChunkRecorder, pickMimeType } from './recorder';
import { closeTranscriptStream, openTranscriptStream } from './transcript-audio';
import { ChunkUploader } from './chunk-upload';
import { useBreakoutClock } from './breakout-clock';
import { applyBreakoutFilter as applyFilter } from './breakout-filter';
import { useNotificationPrefs } from '../data/notifications';
import type * as Livekit from 'livekit-client';
import type {
  Room,
  Track,
  LocalAudioTrack,
  LocalTrackPublication,
  LocalVideoTrack,
  RemoteTrack,
  Participant,
  RemoteParticipant,
  RemoteTrackPublication,
  TrackPublication,
} from 'livekit-client';

/**
 * A HIVAS MOTORJA (livekit-client) KESOBB TOLTODIK BE (K2, 4. csomag).
 *
 * Az app egyharmada volt, es minden indulaskor letoltodott - akkor is, ha
 * aznap senki nem hivott. Most csak az elso csatlakozaskor jon le; a
 * fuggvenyek, amelyek hasznaljak, mind egy mar letezo `Room`-mal dolgoznak,
 * vagyis addigra biztosan betoltott.
 */
let livekitModule: typeof Livekit | null = null;

async function loadLivekit(): Promise<typeof Livekit> {
  livekitModule ??= await import('livekit-client');
  return livekitModule;
}

function lk(): typeof Livekit {
  if (!livekitModule) throw new Error('A hívásmotor még nincs betöltve.');
  return livekitModule;
}
import {
  canTransition,
  HUDDLE_BROADCAST_CHANNEL,
  HUDDLE_HEARTBEAT_INTERVAL_MS,
  HUDDLE_REACTION_TTL_MS,
  huddleDataFrameSchema,
  QUICK_VOTE_ATTRIBUTE,
  QUICK_VOTE_IDLE_MS,
  quickVoteChoices,
  type QuickVote,
  DEFAULT_HOLD_MUSIC,
  DEFAULT_RINGBACK,
  DEFAULT_SOUND,
  HOLD_MUSIC_DELAY_MS,
  isLiveState,
  newId,
  sameBreakoutGroup,
  breakoutGroupOf,
  type Huddle,
  type HuddleBreakout,
  type HuddleDataFrame,
  type HuddleRequestKind,
  type HuddleState,
  type PreflightResult,
} from '@avslack/shared';
import {
  huddleHeartbeat,
  inviteToHuddle,
  cancelHuddleInvite,
  leaveHuddleApi,
  muteHuddleParticipant,
  reportHuddleEvent,
  reportPreflightFailure,
  startHuddle,
  setHuddleTranscribe,
  ApiRequestError,
} from '../lib/api';
import { runPreflight } from './preflight';
import { onHuddleEvicted, onHuddleUpdated } from './bus';
import { huddlesKey, upsertHuddle } from '../data/huddles';
import { isUnreachable, queueOfflineCall } from '../data/outbox';
import { cachedChannelKind } from '../lib/offline-cache';

/**
 * Egy megjelenitendo kep: kamera vagy kepernyo, a tulajdonosaval.
 *
 * A sav lehet tavoli ES sajat is. Kezdetben csak a tavoliak kerultek ide, es
 * ettol a SAJAT kameraképünk sehol nem latszott: a LED vilagitott, a tobbiek
 * lattak a kepet, mi magunk viszont csak a monogramunkat.
 */
interface MediaTile {
  identity: string;
  track: Track;
}

export interface FloatingReaction {
  id: string;
  emoji: string;
  from: string;
  at: number;
}

export interface Stroke {
  target: string;
  color: string;
  points: { x: number; y: number }[];
  from: string;
}

/** Egy nekunk szolo keres a hivasban - lasd `requestFrom`. */
export interface HuddleIncomingRequest {
  id: string;
  kind: HuddleRequestKind;
  /** A kero azonositoja - a LiveKit kezbesitesebol, nem a keretbol. */
  from: string;
}

/** Ennyi ideig all a kerdes a cimzettnel; utana lejartnak szamit. */
const REQUEST_TTL_MS = 30_000;
/** Ennyi ideig latszik a kero visszajelzese. */
const REQUEST_NOTICE_MS = 6_000;

/**
 * Tud-e ez az eszkoz kepernyot megosztani?
 *
 * Telefonos bongeszoben (iOS Safari, Android Chrome) nincs `getDisplayMedia`.
 * Ilyenkor a kerest meg sem mutatjuk - egy "Igen" gomb, ami utana semmit nem
 * csinal, rosszabb a nemnel.
 */
function canShareScreen(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function'
  );
}

/** Egy kiscsoport segitseget kert a hazigazdatol (docs/19). */
export interface BreakoutHelp {
  /** A kero azonositoja. */
  from: string;
  /** Melyik csoportbol. */
  group: number;
  at: number;
}

interface HuddleContextValue {
  state: HuddleState;
  huddle: Huddle | null;
  /**
   * A NYITOTT kiscsoportos idoszak, vagy 
ull (docs/19). A helyi ora szerint mar
   * lejartat nem tartalmazza: a visszatereshez nem kell a szerver valaszara varni.
   */
  breakout: HuddleBreakout | null;
  /** Az idoszak vege a SAJAT ora szerint (ms) - a visszaszamlalas ebbol megy. */
  breakoutEndsAtMs: number | null;
  /** Melyik csoportban vagyok (0 = a foszobaban). */
  myBreakoutGroup: number;
  /** A hazigazdanak: a csoportoktol erkezett segitsegkeresek. */
  helpRequests: BreakoutHelp[];
  /** Segitseget kerunk a hazigazdatol (a kiscsoportbol). */
  requestBreakoutHelp: () => void;
  dismissHelp: (from: string) => void;
  /** A csatorna, amiben eppen bent vagyunk (vagy amibe kapcsolodunk). */
  channelId: string | null;
  error: string | null;
  preflight: PreflightResult | null;
  speakingIds: string[];
  isMuted: boolean;
  isCameraOn: boolean;
  /** Eppen valt a kamera (be vagy ki). */
  isCameraBusy: boolean;
  /** A kamerakep hattere: semmi, elmosas vagy sajat kep. */
  background: VideoBackground;
  /**
   * A sajat mikrofon zajszurese (DeepFilterNet3) - ami TENYLEG fut. Ha a modell
   * ezen a gepen nem indul el, `off`, akkor is, ha a beallitas mas.
   */
  noiseLevel: NoiseLevel;
  isSharingScreen: boolean;
  /**
   * Szuneten vagyunk-e.
   *
   * Bent maradunk a hivasban, de nem szolunk bele es nem is halljuk - kozben
   * mashol lehetunk, akar egy masik olelesben. A tobbiek latjak.
   */
  isPaused: boolean;
  /**
   * TOBB egyideju kepernyomegosztas.
   *
   * Mindenki maga valasztja, MELYIKEKET nezi - egy kozos "most ezt nezzuk"
   * allapot azt jelentene, hogy barki elrantja a tobbiek kepernyojet.
   *
   * Ez a lista tobb elemu lehet: ket megosztast egymas mellett is lehet nezni
   * (osztott kepernyo). Egy termekbemutato es a hozza tartozo tablazat
   * egyszerre kell - kulon-kulon nezve a ketto kozott oda-vissza kapcsolgatas
   * lenne, es epp az osszefugges veszne el.
   */
  screenShares: MediaTile[];
  visibleShares: string[];
  /**
   * Sikerult-e zold keretet tenni a megosztott KIJELZO szelere.
   *
   * Harom kijelzo mellett megosztas kozben nem latszik, melyiket latjak a
   * tobbiek. Ha nem sikerult, a felulet MEGMONDJA, miert - egy nema hianyt
   * a felhasznalo elrontott funkcionak lat.
   */
  shareFrame: ShareFrameState;
  /**
   * A valaszthato kijelzok - CSAK ha kerdeznunk kell.
   *
   * Harom egyforma monitornal a rogzitett kep merete sosem dont, ezert
   * megkerdezzuk. Egyszer: a valasztast megjegyezzuk.
   */
  shareMonitors: Monitor[];
  chooseShareMonitor: (name: string) => Promise<void>;
  cameraTracks: MediaTile[];
  reactions: FloatingReaction[];
  /**
   * Ki jelentkezik: azonosito -> a kezemeles idopontja (ms).
   *
   * Az idopont adja a SORRENDET: aki elobb emelte fel a kezet, az szol
   * elobb. Ez igy mindenkinel ugyanaz, mert a LiveKit resztvevo-adatabol jon.
   */
  raisedHands: Record<string, number>;
  /** Kezemeles / leengedes. Gyorsgombja a Ctrl+H. */
  toggleHand: () => void;
  /** Gyorsszavazas: ki mire nyomott (identity -> pipa/X). */
  quickVotes: Record<string, QuickVote>;
  /** Sajat gyorsszavazat. Ugyanarra ujra nyomva visszavonja. */
  castQuickVote: (vote: QuickVote) => void;
  /** A gyorsszavazas nullazasa MINDENKINEL (barki megteheti). */
  clearQuickVotes: () => void;
  /** Ki van "mindjart jovok" allapotban: azonosito -> a tavozas idopontja (ms). */
  awayIds: Record<string, number>;
  /** Mindjart jovok / visszajottem. Tavozaskor a mikrofon elnemul. */
  toggleAway: () => void;
  strokes: Stroke[];
  devices: { deviceId: string; label: string }[];
  activeDeviceId: string | null;
  /**
   * A HANGSZORO kulon eszkoz.
   *
   * A mikrofon es a hangszoro gyakran NEM ugyanaz: fejhallgatoval veszek fel,
   * de a hangfalon hallgatom - vagy forditva. Amig egyetlen valaszto volt,
   * ez a kombinacio nem volt beallithato.
   */
  outputDevices: { deviceId: string; label: string }[];
  activeOutputId: string | null;
  /**
   * A KAMERA is kulon eszkoz.
   *
   * Beepitett kamera + kulso webkamera egyutt a leggyakoribb parositas, es a
   * ketto kozott a rendszer beallitasaiban valtani a hivas kozben nehezkes.
   *
   * A lista csak azutan kap neveket, hogy a kamerat egyszer bekapcsoltad: a
   * bongeszo a kamerak nevet KAMERA-engedely nelkul nem arulja el (a
   * mikrofone nem eleg hozza).
   */
  videoDevices: { deviceId: string; label: string }[];
  activeCameraId: string | null;
  /** Tud-e a bongeszo hangkimenetet valtani. Firefoxban es Safariban peldaul nem. */
  canChooseOutput: boolean;
  /**
   * A bongeszo LETILTOTTA a hang lejatszasat.
   *
   * Ilyenkor a hivas el, a resztvevok latszanak - es NEM HALLATSZIK SEMMI.
   * Ez a legrosszabb fajta hiba: ugy nez ki, mintha minden rendben lenne.
   * Egyetlen kattintas feloldja, de tudni kell rola.
   */
  isAudioBlocked: boolean;
  enableAudio: () => Promise<void>;

  /** `answer`: egy bejovo hivas felvetele - ha mar mas felvette, a szerver elutasitja. */
  join: (
    channelId: string,
    title?: string,
    options?: { answer?: boolean; quiet?: boolean },
  ) => Promise<void>;
  leave: () => void;
  toggleMute: () => void;
  toggleCamera: () => Promise<void>;
  /** Hatter valasztasa. Kikapcsolt kameranal a kovetkezo bekapcsolaskor lep ervenybe. */
  setBackground: (background: VideoBackground) => Promise<void>;
  /** Zajszures szintje. Hivason kivul csak megjegyzi - a kovetkezo belepeskor lep ervenybe. */
  setNoiseLevel: (level: NoiseLevel) => Promise<void>;
  togglePause: () => Promise<void>;
  toggleScreenShare: () => Promise<void>;
  /**
   * A megosztott tartalom CSERE megosztas kozben: masik ablak vagy kijelzo, a megosztas
   * leallitasa es ujrainditasa nelkul. Ha a felhasznalo megsem valaszt, a regi marad.
   */
  switchScreenShare: () => Promise<void>;
  switchDevice: (deviceId: string) => Promise<void>;
  switchOutput: (deviceId: string) => Promise<void>;
  switchCamera: (deviceId: string) => Promise<void>;
  /** Be/ki kapcsolja EGY megosztas mutatasat - a tobbit nem bantja. */
  toggleShare: (identity: string) => void;
  sendReaction: (emoji: string) => void;
  sendStroke: (stroke: Omit<Stroke, 'from'>) => void;
  clearStrokes: (target: string) => void;
  muteOther: (userId: string) => Promise<void>;
  /**
   * Kit mennyire halkan hallunk - CSAK NALUNK.
   *
   * Nem ugyanaz, mint a `muteOther`: az a szerverre megy es MINDENKINEL
   * elnemiti az illetot; ez a sajat lejatszasunkat allitja, es a masik fel
   * semmit nem vesz eszre belole. Ez a valasz arra, hogy a hivasban nem
   * mindenki egyforma hangos - eddig erre csak a teljes elnemitas volt.
   *
   * 1 = valtozatlan, 0 = nema, a felso hatar a `MAX_VOLUME` (a halkan
   * beszelot fel is kell tudni hozni). A beallitas hivasok kozott megmarad.
   */
  participantVolumes: Record<string, number>;
  setParticipantVolume: (userId: string, volume: number) => void;
  invite: (userIds: string[]) => Promise<void>;
  /** A meg fel nem vett behivas visszavonasa (a hivjuk csempe gombja). */
  cancelInvite: (userId: string) => Promise<void>;
  /**
   * KERES egy resztvevotol: ossza meg a kepernyojet, vagy kapcsolja vissza a
   * mikrofonjat. O dont - nala Igen/Nem jelenik meg ({@link incomingRequest}).
   *
   * Miert keres, es nem tavvezerles? Mert a kepernyomegosztast a bongeszo csak
   * a SAJAT kattintasra engedi (es azt is o valasztja ki, mit oszt meg), a
   * mikrofont pedig senki mas nem kapcsolhatja be - lasd a nemitas vegpontjat.
   */
  requestFrom: (userId: string, kind: HuddleRequestKind) => void;
  /** Egy nekunk szolo keres, amire meg nem valaszoltunk. */
  incomingRequest: HuddleIncomingRequest | null;
  answerRequest: (accept: boolean) => void;
  /** Visszajelzes a SAJAT kereseinkrol: elment, elfogadtak, elutasitottak. */
  requestNotice: string | null;
  dismissRequestNotice: () => void;
  /**
   * "Uj chat-uzenet erkezett" szamlaloja.
   *
   * A hivas ablakaban nincs valos ideju kapcsolat, ezert a chat rendszeresen
   * lekerdez. Ez a szamlalo a GYORS ut: a media-adatcsatornan erkezo jelzesre
   * no, es a chat-panel ilyenkor azonnal frissit. Tartalmat SOSEM hoz - azt a
   * szervertol kerjuk.
   */
  chatSignal: number;
  /** Szolunk a tobbieknek, hogy uj chat-uzenet van. */
  notifyChat: () => void;
  /**
   * Keszul-e atirat errol a hivasrol.
   *
   * A huddle allapotabol jon, tehat MINDENKI ugyanazt latja - egy felvetelt
   * nem lehet titokban elinditani.
   */
  isTranscribing: boolean;
  /** Eppen most rogzit-e a sajat mikrofonunk. */
  isRecording: boolean;
  toggleTranscribe: () => Promise<void>;
  dismissError: () => void;
}

const HuddleContext = createContext<HuddleContextValue | null>(null);

/**
 * NINCS KAPCSOLAT, a hivas el sem indult (K2).
 *
 * A hivott errol eddig semmit nem tudott meg. Kozvetlen hivasnal most
 * feljegyezzuk, es amint ujra van kapcsolat, "Nem fogadott hivas" jelzest kap
 * (data/outbox.ts). A visszaadott szoveg ezt mondja el a hivonak.
 */
export const OFFLINE_CALL_FAILED = 'Nincs kapcsolat, a hívás nem indult el.';

/** Lasd `pendingJoinRef`. */
interface PendingJoin {
  /** A szerver valasza utan ismert; addig meg nincs mihez kotni az esemenyt. */
  huddleId: string | null;
  outcome: 'join_success' | 'join_failed' | 'join_cancelled' | null;
}

/**
 * A csatlakozas kimenetenek jelentese - csatlakozasonkent EGYSZER. A kesobbi
 * hivasok (pl. a lebontas a mar sikeres csatlakozas utan) nem csinalnak semmit.
 */
function settleJoin(
  ticket: PendingJoin | null,
  outcome: NonNullable<PendingJoin['outcome']>,
  detail?: string,
  durationMs?: number,
): void {
  if (!ticket || ticket.outcome) return;
  ticket.outcome = outcome;
  if (!ticket.huddleId) return;
  void reportHuddleEvent(ticket.huddleId, {
    kind: outcome,
    ...(detail ? { detail: detail.slice(0, 200) } : {}),
    ...(durationMs !== undefined ? { durationMs } : {}),
  }).catch(() => undefined);
}

function offlineCallMessage(queryClient: QueryClient, channelId: string): string {
  const kind =
    queryClient
      .getQueryData<{ id: string; kind: string }[]>(['channels'])
      ?.find((channel) => channel.id === channelId)?.kind ?? cachedChannelKind(channelId);
  const meId = queryClient.getQueryData<{ user: { id: string } }>(['session'])?.user.id;
  if ((kind === 'dm' || kind === 'group_dm') && meId) {
    queueOfflineCall({
      channelId,
      clientId: newId(),
      authorId: meId,
      attemptedAt: new Date().toISOString(),
    });
    return `${OFFLINE_CALL_FAILED} Amint újra lesz, a hívott értesítést kap, hogy kerested.`;
  }
  return OFFLINE_CALL_FAILED;
}

/** A sajat csempenk levetele egy listarol - kamera vagy megosztas lekapcsolasakor. */
/** A sajat, eppen elo kamerasav - az elmosas ezen dolgozik. */
function localCameraTrack(room: Room): LocalVideoTrack | null {
  for (const publication of room.localParticipant.videoTrackPublications.values()) {
    if (publication.source === lk().Track.Source.Camera && publication.track) {
      return publication.track as LocalVideoTrack;
    }
  }
  return null;
}

/**
 * A sajat, eppen megosztott kepernyosav.
 *
 * A KERET ebbol tudja meg, mekkora a rogzitett forras - abbol pedig az
 * asztali app, hogy melyik fizikai kijelzorol van szo.
 */
function localScreenTrack(room: Room): MediaStreamTrack | null {
  for (const publication of room.localParticipant.videoTrackPublications.values()) {
    if (publication.source === lk().Track.Source.ScreenShare && publication.track) {
      return publication.track.mediaStreamTrack;
    }
  }
  return null;
}

/**
 * A sajat, elo mikrofonsav.
 *
 * Ebbol keszul a felvetel. Nem a `getUserMedia`-t hivjuk ujra: a hivas mar
 * megnyitotta az eszkozt, es egy masodik megnyitas nemcsak pazarlas lenne,
 * hanem nemely gepen el is venne a mikrofont a hivastol.
 */
function localMicrophoneAudioTrack(room: Room): LocalAudioTrack | null {
  for (const publication of room.localParticipant.audioTrackPublications.values()) {
    if (publication.source === lk().Track.Source.Microphone && publication.track) {
      return publication.track as LocalAudioTrack;
    }
  }
  return null;
}

function localMicrophoneTrack(room: Room): MediaStreamTrack | null {
  for (const publication of room.localParticipant.audioTrackPublications.values()) {
    if (publication.source === lk().Track.Source.Microphone && publication.track) {
      return publication.track.mediaStreamTrack;
    }
  }
  return null;
}

function removeLocalTile(
  room: Room,
  setTiles: React.Dispatch<React.SetStateAction<MediaTile[]>>,
): void {
  const identity = room.localParticipant.identity;
  setTiles((current) => current.filter((item) => item.identity !== identity));
}

/** Meddig varunk a kamerara, mielott hibat jelzunk. */
const CAMERA_TIMEOUT_MS = 8_000;

/** Ez a ful azonositoja. Egy felhasznalo egyszerre EGY fulon lehet huddle-ben. */
/**
 * Tud-e a bongeszo hangkimenetet valtani?
 *
 * **Az `AudioContext`-et is meg kell kerdezni**, nem csak a lejatszo elemet: a
 * hang Web Audio uton szol, tehat a valtas AZON mulik. Ez elo hivasban derult
 * ki - Firefoxban a lejatszo elem ismeri a `setSinkId`-t, az `AudioContext`
 * viszont nem, es a resztvevo egy nyers hibauzenetet kapott
 * ("cannot switch audio output, the current browser does not support it")
 * egy olyan valasztotol, amit fel sem kellett volna kinalni neki.
 *
 * Ahol barmelyik hianyzik, a valaszto helyett megmondjuk, hol lehet allitani.
 * Egy gomb, ami latszolag nem csinal semmit, rosszabb, mintha ott sem lenne.
 */
function canChooseOutput(): boolean {
  if (typeof HTMLMediaElement === 'undefined' || typeof AudioContext === 'undefined') return false;
  return 'setSinkId' in HTMLMediaElement.prototype && 'setSinkId' in AudioContext.prototype;
}

/**
 * A valasztas megjegyzese - a NEVVEL egyutt.
 *
 * A nev a masodik esely a kesobbi felismeresre: ha a bongeszo ujrasorsolja az
 * azonositokat (mert kozben megszunt es ujra megkaptuk az engedelyt), a nev
 * alapjan meg mindig megtalaljuk ugyanazt az eszkozt. Lasd `devices.ts`.
 */
function remember(kind: DeviceKind, deviceId: string, list: Device[]): void {
  const device = list.find((item) => item.deviceId === deviceId);
  if (device) rememberDevice(kind, device);
}

const SESSION_ID = `s-${Math.random().toString(36).slice(2, 10)}`;

/**
 * A kezemeles a LiveKit RESZTVEVO-ADATABAN el, nem az adatcsatornan.
 *
 * Egy reakcio mulandó - ha lemaradtal rola, nem baj. A felemelt kez viszont
 * ALLAPOT: aki kesobb lep be, annak is latnia kell, hogy valaki szolni
 * szeretne. A resztvevo adatait a LiveKit a kesobb belepoknek is atadja, es
 * kilepeskor magatol eltunteti - tehat nem ragadhat fent egy kez valakirol,
 * aki mar nincs bent.
 *
 * Az ertek a felemeles idopontja (ms), ures szoveg = leengedve.
 */
const HAND_ATTRIBUTE = 'avslack.hand';

function handOf(participant: Participant): number {
  return Number(participant.attributes[HAND_ATTRIBUTE]) || 0;
}

/**
 * GYORSSZAVAZAT ('yes' / 'no', ures = nincs).
 *
 * Ugyanaz az ut, mint a kezemelesnel: ALLAPOT a resztvevo adataban - a kesobb
 * belepo is latja az allast, kilepeskor pedig a szavazat magatol eltunik.
 */
const VOTE_ATTRIBUTE = QUICK_VOTE_ATTRIBUTE;

function voteOf(participant: Participant): QuickVote | null {
  const value = participant.attributes[VOTE_ATTRIBUTE];
  return quickVoteChoices.includes(value as QuickVote) ? (value as QuickVote) : null;
}

/**
 * "Mindjart jovok" - a resztvevo bent van, de eppen nincs a gepnel.
 *
 * Ugyanaz az ut, mint a kezemelesnel: ALLAPOT, nem esemeny, ezert a resztvevo-
 * adatban el - a kesobb belepo is latja, es kilepeskor magatol eltunik.
 *
 * Az ertek a tavozas idopontja (ms), ures szoveg = visszajott.
 */
const AWAY_ATTRIBUTE = 'avslack.away';

function awayOf(participant: Participant): number {
  return Number(participant.attributes[AWAY_ATTRIBUTE]) || 0;
}

interface BroadcastClaim {
  type: 'claim';
  sessionId: string;
  huddleId: string;
}

export function HuddleProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const [state, setState] = useState<HuddleState>('idle');
  const [huddle, setHuddle] = useState<Huddle | null>(null);
  const [channelId, setChannelId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preflight, setPreflight] = useState<PreflightResult | null>(null);
  const [speakingIds, setSpeakingIds] = useState<string[]>([]);
  const [isMuted, setIsMuted] = useState(true);
  const [isCameraOn, setIsCameraOn] = useState(false);
  /** Eppen valt a kamera - addig a gomb nem fogad ujabb kattintast. */
  const [isCameraBusy, setIsCameraBusy] = useState(false);
  const cameraBusyRef = useRef(false);
  /** Hatter-elmosas. A beallitast megjegyezzuk - lasd `huddle/blur.ts`. */
  const [background, setBackgroundState] = useState<VideoBackground>(backgroundPreference);
  /** Zajszures. A beallitas gepenkent megjegyezve - lasd `huddle/noise.ts`. */
  const [noiseLevel, setNoiseLevelState] = useState<NoiseLevel>(noisePreference);
  /** A telefonos hivo(k) hangjan futo szurok, savonkent (lasd `applyRemoteNoiseFilter`). */
  const phoneFiltersRef = useRef(new Map<string, RemoteNoiseFilter>());
  /** A KIVANT szint (a beallitas) - az uj mikrofonsav ezt kapja, nem a tenylegesen futot. */
  const noiseWantedRef = useRef<NoiseLevel>(noisePreference());
  const [isSharingScreen, setIsSharingScreen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [chatSignal, setChatSignal] = useState(0);
  const [incomingRequest, setIncomingRequest] = useState<HuddleIncomingRequest | null>(null);
  const [requestNotice, setRequestNotice] = useState<string | null>(null);
  /** A sajat, meg megvalaszolatlan kereseink: azonosito -> kinek, mit. */
  const outgoingRef = useRef(
    new Map<string, { to: string; kind: HuddleRequestKind; timer: number }>(),
  );
  const noticeTimerRef = useRef<number | null>(null);
  const incomingTimerRef = useRef<number | null>(null);
  /**
   * A mikrofonsav "nemzedeke".
   *
   * Akkor no, amikor a sajat mikrofonsavunk letrejon vagy visszakapcsol. A
   * felvetel ebbol tudja, hogy UJRA meg kell probalnia - lasd a felvetel
   * hatasat lentebb, ahol ez egy valos, nehezen eszrevett hibat javit.
   */
  const [micGeneration, setMicGeneration] = useState(0);
  /** A telefonos hivo hangsavja megjelent vagy eltunt - a felvetele ettol indul ujra. */
  const [phoneTrackGeneration, setPhoneTrackGeneration] = useState(0);
  const [isAudioBlocked, setIsAudioBlocked] = useState(false);
  const [screenShares, setScreenShares] = useState<MediaTile[]>([]);
  const [cameraTracks, setCameraTracks] = useState<MediaTile[]>([]);
  const [visibleShares, setVisibleShares] = useState<string[]>([]);

  /**
   * Egy megosztas be- vagy kikapcsolasa.
   *
   * KAPCSOLO, nem valto: a masodik megosztas bekapcsolasa nem tunteti el az
   * elsot, hanem melle all - igy lehet ket kepernyot egyszerre nezni. Ez a
   * kulonbseg az egesz funkcio lenyege, ezert all itt kulon fuggvenyben.
   *
   * A sorrend a bekapcsolas sorrendje: aki masodikkent kerult be, az kerul
   * jobbra. Igy a kep nem ugral at, amikor egy harmadik is beszall.
   */
  const toggleShare = useCallback((identity: string) => {
    setVisibleShares((current) =>
      current.includes(identity) ? current.filter((id) => id !== identity) : [...current, identity],
    );
  }, []);
  const [shareFrame, setShareFrame] = useState<ShareFrameState>('off');
  const [shareMonitors, setShareMonitors] = useState<Monitor[]>([]);
  const [reactions, setReactions] = useState<FloatingReaction[]>([]);
  const [raisedHands, setRaisedHands] = useState<Record<string, number>>({});
  /**
   * A felemelt kezek REFBEN is: a pittyhez tudnunk kell, hogy a valtozas
   * felemeles volt-e (0 -> ido), es ezt a LiveKit-esemeny kezeloje nem
   * olvashatja a React allapotabol - az nem ujul meg vele.
   */
  const handsRef = useRef<Record<string, number>>({});
  const [quickVotes, setQuickVotes] = useState<Record<string, QuickVote>>({});
  const quickVotesRef = useRef<Record<string, QuickVote>>({});
  const [awayIds, setAwayIds] = useState<Record<string, number>>({});
  const awayRef = useRef<Record<string, number>>({});
  /** Szolt-e a mikrofon, amikor "mindjart jovok"-ra mentunk - visszaterve ezt allitjuk vissza. */
  const micBeforeAwayRef = useRef(false);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [devices, setDevices] = useState<{ deviceId: string; label: string }[]>([]);
  const [activeDeviceId, setActiveDeviceId] = useState<string | null>(null);
  const [outputDevices, setOutputDevices] = useState<{ deviceId: string; label: string }[]>([]);
  const [activeOutputId, setActiveOutputId] = useState<string | null>(null);
  const [videoDevices, setVideoDevices] = useState<{ deviceId: string; label: string }[]>([]);
  const [activeCameraId, setActiveCameraId] = useState<string | null>(null);
  /**
   * A valasztott hangszoro REFBEN is.
   *
   * A KESOBB belepok hangja is ide kell szoljon. A rakotest vegzo fuggveny nem
   * ujul meg a React allapotaval, ezert olvasnia kell egy refet - kulonben a
   * valasztas csak azokra a resztvevokre ervenyesulne, akik mar bent voltak.
   */
  const outputIdRef = useRef<string | null>(null);

  const roomRef = useRef<Room | null>(null);
  const stateRef = useRef<HuddleState>('idle');
  const huddleRef = useRef<Huddle | null>(null);
  const audioRef = useRef<HTMLDivElement | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const broadcastRef = useRef<BroadcastChannel | null>(null);
  /**
   * A szunet allapota REFBEN is.
   *
   * Ket helyrol kell elerni olyan fuggvenybol, ami nem ujul meg a React
   * allapotaval: a masik ablak igenybejelentesenek kezelojebol es a hang
   * rakotesebol. Ha ezek elavult erteket latnanak, a szuneteltetett hivas
   * bezarna magat vagy megszolalna - vagyis pont az ellenkezojet tenne.
   */
  const pausedRef = useRef(false);
  /**
   * A szunet kapcsoloja REFBEN - a tobbi ablak igenybejelentese ebbol all
   * szunetre (lasd a BroadcastChannel kezelojet). A kezelo egyszer jon letre,
   * a kapcsolo viszont a kamera/megosztas allapotaval egyutt ujul.
   */
  const togglePauseRef = useRef<() => Promise<void>>(async () => undefined);

  /**
   * Kit mennyire halkan hallunk - CSAK NALUNK (lasd `participant-volume.ts`).
   *
   * Refben is, ugyanabbol az okbol, mint a hangszoro-valasztas: a hang
   * rakotese nem ujul meg a React allapotaval, tehat a KESOBB belepok
   * hangereje csak innen allhat helyre. Enelkul a beallitas pontosan addig
   * ervenyesulne, amig az illeto ki nem esik es vissza nem jon.
   */
  const [participantVolumes, setParticipantVolumes] = useState<Record<string, number>>(() =>
    loadVolumes(),
  );
  const volumesRef = useRef(participantVolumes);
  volumesRef.current = participantVolumes;

  const joinStartedAt = useRef(0);
  /**
   * A FOLYAMATBAN LEVO csatlakozas jegye.
   *
   * A szerver a belepesi jegy kiadasakor `join_attempt`-et ir; minden ilyen
   * probalkozasnak PONTOSAN EGY kimenetet kell jelentenunk: sikert, kudarcot,
   * vagy - ha a felhasznalo maga hagyta abba (bontott, masik ful vette at,
   * bezarta az ablakot) - megszakitast. Ami kimenet nelkul marad, az a
   * sikeraranyban kudarcnak latszik: 2026-11-03-an egyetlen ilyen eset egesz
   * delutan riasztott.
   */
  const pendingJoinRef = useRef<PendingJoin | null>(null);
  const recorderRef = useRef<ChunkRecorder | null>(null);
  const uploaderRef = useRef<ChunkUploader | null>(null);
  const [isRecording, setIsRecording] = useState(false);

  stateRef.current = state;
  huddleRef.current = huddle;

  /*
    KISCSOPORTOS IDOSZAK (docs/19-kiscsoportos-szobak.html).

    Az idoszak a huddle pillanatkepenek resze. A LEJARATOT a kliens maga is
    figyeli (a szerver lezaro munkasa csak a biztosito) - lasd a hookot.
  */
  const { breakout, endsAtMs: breakoutEndsAtMs } = useBreakoutClock(huddle?.breakout ?? null);
  const breakoutRef = useRef<HuddleBreakout | null>(null);
  breakoutRef.current = breakout;
  const breakoutActive = breakout !== null;

  /** A hazigazdanak erkezett, meg el nem intezett segitsegkeresek (kiscsoportokbol). */
  const [helpRequests, setHelpRequests] = useState<BreakoutHelp[]>([]);
  // Az idoszak vegevel a keresek is elavulnak.
  useEffect(() => {
    if (!breakoutActive) setHelpRequests([]);
  }, [breakoutActive]);

  /** A feliratkozas igazitasa a csoporthoz (lasd `breakout-filter.ts`). */
  const applyBreakoutFilter = useCallback(
    (room: Room) => applyFilter(room, breakoutRef.current),
    [],
  );
  const breakoutAssignmentsKey = breakout
    ? `${breakout.id}:${JSON.stringify(breakout.assignments)}`
    : '';
  useEffect(() => {
    const room = roomRef.current;
    if (room && isLiveState(state)) applyBreakoutFilter(room);
  }, [breakoutAssignmentsKey, state, applyBreakoutFilter]);

  const myBreakoutGroup = (() => {
    const me = roomRef.current?.localParticipant.identity;
    return me ? breakoutGroupOf(breakout, me) : 0;
  })();

  /**
   * Allapotvaltas.
   *
   * Az ervenytelen atmenetet NEM hajtjuk vegre, de naplozzuk - igy egy
   * fejlesztoi hiba nem ragasztja be a felhasznalot egy hivasba. A `forceIdle`
   * a veszkijarat: az mindig sikerul.
   */
  const transition = useCallback((next: HuddleState): boolean => {
    const current = stateRef.current;
    if (current === next) return true;
    if (!canTransition(current, next)) {
      console.warn(`[huddle] ervenytelen atmenet: ${current} -> ${next}`);
      return false;
    }
    stateRef.current = next;
    setState(next);
    return true;
  }, []);

  /**
   * A hivas HELYI lebontasa - feltetel nelkul.
   *
   * Ez a valasz arra, hogy "nem lehet kilepni a huddle-bol". A felulet bezarul
   * es a mikrofon leall AKKOR IS, ha a szerver nem valaszol, ha a LiveKit
   * beragadt, vagy ha barmi mas hiba tortent. A szerver-oldali takaritas
   * utolag, kulon uton fut.
   */
  const teardown = useCallback((message: string | null) => {
    // Ha meg csatlakoztunk, es eddig nem dolt el a kimenet, akkor ez megszakitas.
    settleJoin(pendingJoinRef.current, 'join_cancelled', message ?? 'Kilépés csatlakozás közben');
    pendingJoinRef.current = null;

    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    for (const filter of phoneFiltersRef.current.values()) filter.stop();
    phoneFiltersRef.current.clear();

    // A FELVETEL ALL LE ELOSZOR: a mikrofon elengedese elott meg ki kell
    // adnia az utolso darabot.
    recorderRef.current?.stop();
    recorderRef.current = null;
    uploaderRef.current = null;
    setIsRecording(false);

    const room = roomRef.current;
    roomRef.current = null;
    if (room) {
      room.removeAllListeners();
      // Nem varunk ra: a bontas nem foghatja meg a feluletet.
      void room.disconnect().catch(() => undefined);
    }

    if (audioRef.current) audioRef.current.replaceChildren();

    stateRef.current = 'idle';
    setState('idle');
    setHuddle(null);
    setChannelId(null);
    setSpeakingIds([]);
    setScreenShares([]);
    setCameraTracks([]);
    setVisibleShares([]);
    setReactions([]);
    handsRef.current = {};
    setRaisedHands({});
    quickVotesRef.current = {};
    setQuickVotes({});
    awayRef.current = {};
    setAwayIds({});
    setStrokes([]);
    for (const pending of outgoingRef.current.values()) window.clearTimeout(pending.timer);
    outgoingRef.current.clear();
    if (incomingTimerRef.current !== null) window.clearTimeout(incomingTimerRef.current);
    setIncomingRequest(null);
    setRequestNotice(null);
    setIsSharingScreen(false);
    setShareFrame('off');
    setShareMonitors([]);
    void hideShareFrame();
    setIsCameraOn(false);
    setIsMuted(true);
    setIsPaused(false);
    pausedRef.current = false;
    outputIdRef.current = null;
    setActiveOutputId(null);
    setIsAudioBlocked(false);
    if (message) setError(message);
  }, []);

  // ─── Kilepes ────────────────────────────────────────────────────────────────

  const leave = useCallback(() => {
    const current = huddleRef.current;

    // A sorrend szandekos: ELOSZOR bontunk helyben, utana szolunk a szervernek.
    transition('leaving');
    teardown(null);

    if (current) {
      void leaveHuddleApi(current.id, SESSION_ID).catch(() => undefined);
      void reportHuddleEvent(current.id, { kind: 'left' }).catch(() => undefined);
    }
  }, [teardown, transition]);

  /**
   * Veszkijarat: ketszeri Esc.
   *
   * Ha barmi beragadna, ez akkor is kienged. Szandekosan a legegyszerubb ut:
   * nem fugg a szervertol, a LiveKittol, sem a tobbi allapottol.
   */
  useEffect(() => {
    let lastEscape = 0;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (stateRef.current === 'idle') return;

      const now = Date.now();
      if (now - lastEscape < 800) {
        lastEscape = 0;
        leave();
      } else {
        lastEscape = now;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [leave]);

  // ─── Egy huddle felhasznalonkent (tobb ful) ─────────────────────────────────

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;

    const channel = new BroadcastChannel(HUDDLE_BROADCAST_CHANNEL);
    broadcastRef.current = channel;

    channel.onmessage = (event: MessageEvent<BroadcastClaim>) => {
      const claim = event.data;
      if (claim?.type !== 'claim' || claim.sessionId === SESSION_ID) return;
      if (!huddleRef.current) return;

      /*
        A SZUNETELTETETT hivas tulel egy masik hivast.

        Eppen ez a szunet ertelme: "atmegyek egy masik megbeszelesre, de ebbol
        nem lepek ki". A mikrofonunk es a hangunk mar ki van kapcsolva, tehat
        semmi nem szolhat ket helyen egyszerre - a bezaras itt nem vedene
        semmit, csak elveszitene a hivast, amibe vissza akarunk terni.
      */
      if (pausedRef.current) return;

      /*
        MASIK hivasba leptunk (felvettunk egy bejovo hivast, vagy csatlakoztunk
        egy masik olelesbe): ez a hivas SZUNETRE all, nem zarul be.

        Korabban itt is bezartuk - es a felhasznalo ugy vesztette el a
        megbeszeleset, hogy csak egy telefont vett fel kozben. A szunet pontosan
        erre valo: bent maradunk, nem szolunk bele es nem is halljuk, a tobbiek
        latjak, hogy szuneten vagyunk - es egy kattintassal visszaterhetunk.
      */
      if (claim.huddleId !== huddleRef.current.id) {
        void togglePauseRef.current();
        return;
      }

      // UGYANEZT a hivast vette at egy masik ful. Itt csendben bezarjuk - a
      // mikrofon nem maradhat bekapcsolva ket helyen.
      teardown('Az ölelést egy másik ablakban vetted át.');
    };

    return () => {
      channel.close();
      broadcastRef.current = null;
    };
  }, [teardown]);

  // A szerver is jelezhet kileptetest (pl. masik GEPRŐL vettek at).
  useEffect(
    () =>
      onHuddleEvicted((huddleId, reason) => {
        if (huddleRef.current?.id !== huddleId) return;
        teardown(reason);
      }),
    [teardown],
  );

  /**
   * A hivas allapota a VALOS IDEJU retegbol.
   *
   * Enelkul csak az eletjel (20 masodperc) hozna at a valtozast, es a
   * felulet husz masodpercig hazudna: a nemitas ikonja kesve jelenne meg, egy
   * elutasitott hivas pedig ugy latszana, mintha meg csorogne. Egy hivasban
   * husz masodperc nagyon hosszu ido.
   */
  useEffect(
    () =>
      onHuddleUpdated((fresh) => {
        if (huddleRef.current?.id !== fresh.id) return;
        huddleRef.current = fresh;
        setHuddle(fresh);
      }),
    [],
  );

  /**
   * Varakozo zene, amig egyedul vagyunk.
   *
   * Egy ures hivas nyomaszto: nem derul ki, hogy el-e a kapcsolat, vagy csak
   * nincs ott meg senki. Egy halk akkordmenet ezt oldja fel.
   *
   * ─── Ket kulonbozo varakozas, ket kulonbozo idozites ──────────────────────
   *
   * Ha KONKRETAN valakire varunk (kihivtuk, meg nem lepett be), AZONNAL szol a
   * VISSZACSENGETES - az a csorges, amit a telefonban is hallunk hivas kozben.
   * Kulon beallitas, kulon hang: nem ugyanaz, mint amit akkor hallunk, ha
   * MINKET hivnak, es nem is a dallamos varakozo zene. (Aki megis azt szeretne,
   * a "Várakozó zene" valasztast allitva megkapja.)
   *
   * Ha csak egyedul ulunk a hivasban (senkit nem hivtunk ki), marad a zene a
   * {@link HOLD_MUSIC_DELAY_MS} kesleltetessel: ott a hang tolakodo lenne
   * rogton.
   */
  const { data: soundPrefs } = useNotificationPrefs();
  const holdMusic = soundPrefs?.holdMusic ?? DEFAULT_HOLD_MUSIC;
  const ringback = soundPrefs?.ringback ?? DEFAULT_RINGBACK;

  const joinedCount = huddle?.participants.filter((p) => p.state === 'joined').length ?? 0;
  const invitedCount = huddle?.participants.filter((p) => p.state === 'invited').length ?? 0;
  /*
    A TELEFONOS fel is tarsasag: nincs felhasznaloja, igy a resztvevok kozott
    nem szamolodik, de amig csorog vagy beszel, nem vagyunk egyedul. Enelkul
    telefonalas kozben egy ido utan elindult a varakozo zene.
  */
  const phoneLive = huddle?.phoneLegs.some((leg) => leg.state !== 'ended') ?? false;
  // Szunet alatt nincs varakozo zene: eppen azert szuneteltetunk, hogy ez a
  // hivas ne hallatsszon.
  // A bent ulo vendeg (H2) is tarsasag - mellette nem szolhat a varakozo zene.
  const guestLive = huddle?.guests?.some((guest) => guest.state === 'admitted') ?? false;
  const alone = isLiveState(state) && joinedCount <= 1 && !phoneLive && !guestLive && !isPaused;
  const waitingForSomeone = alone && invitedCount > 0;

  useEffect(() => {
    if (!alone) {
      stopHoldMusic();
      stopRingback();
      return;
    }

    if (waitingForSomeone) {
      if (ringback === 'zene') startHoldMusic(holdMusic);
      else startRingback(ringback);

      return () => {
        stopRingback();
        stopHoldMusic();
      };
    }

    const timer = setTimeout(() => startHoldMusic(holdMusic), HOLD_MUSIC_DELAY_MS);
    return () => {
      clearTimeout(timer);
      stopHoldMusic();
    };
  }, [alone, waitingForSomeone, holdMusic, ringback]);

  /**
   * BELEPETT / KILEPETT - hangjelzes.
   *
   * Ez volt a keres lenyege: amig varunk valakire, elkezdunk mast csinalni, es
   * nem vesszuk eszre, hogy belepett - vagy hogy megszakitotta a hivast. A
   * zene azt mondja el, hogy MEG varunk; ez a ket rovid hang azt, hogy mar nem.
   *
   * Meglevo, mar licencelt ertesito hangokat hasznalunk (AOSP, Apache-2.0),
   * nem toltunk le ujakat: igy a rendszer hangvilaga is egyben marad.
   *
   * A BELEPESKOR latott resztvevoket nem jelentjuk be: aki egy mar folyo
   * hivasba lep be, annak nem kell vegighallgatnia mindenkit, aki mar bent van.
   */
  const seenParticipants = useRef<Map<string, string> | null>(null);

  useEffect(() => {
    if (!isLiveState(state)) {
      seenParticipants.current = null;
      return;
    }

    const now = new Map((huddle?.participants ?? []).map((p) => [p.user.id, p.state]));
    const before = seenParticipants.current;
    seenParticipants.current = now;

    // Elso megfigyeles: csak jegyzetelunk, nem szolunk.
    if (!before) return;

    for (const [userId, participantState] of now) {
      if (participantState === 'joined' && before.get(userId) !== 'joined') {
        playNotificationSound('harang');
      }
    }
    for (const userId of before.keys()) {
      if (!now.has(userId)) playNotificationSound('kettos');
    }
  }, [huddle?.participants, state]);

  /**
   * BEJOVO KERES - hangjelzes.
   *
   * A hivas ablaka gyakran a hatterben van (a megosztott ablak vagy a
   * jegyzet mogott), a kerdes pedig 30 masodperc mulva lejar. Hang nelkul
   * epp azt nem venne eszre az ember, hogy most tole varnak valamit.
   *
   * A felhasznalo SAJAT ertesito hangja szol, es csak ha a hangjelzest nem
   * kapcsolta ki - ez nekunk szolo jelzes, mint egy DM.
   */
  const incomingRequestId = incomingRequest?.id ?? null;
  useEffect(() => {
    if (!incomingRequestId) return;
    if (soundPrefs && !soundPrefs.soundEnabled) return;
    playNotificationSound(soundPrefs?.soundName ?? DEFAULT_SOUND);
  }, [incomingRequestId, soundPrefs]);

  /**
   * MIND A HAROM eszkozlista, egyetlen lekerdezesbol.
   *
   * A mikrofon, a hangszoro es a kamera fuggetlen eszkozok, es a felhasznalo
   * gyakran mast valaszt mindharomra. Egyszerre frissitjuk oket, mert a
   * bongeszo ugyis egyetlen listat ad vissza.
   *
   * **A nevek engedelyhez kotottek**, es fajtankent KULON engedelyhez: a
   * hangeszkozok neve mikrofon-engedely utan latszik, a kamerake viszont csak
   * kamera-engedely utan. Ezert lehet ures a kamera-lista addig, amig egyszer
   * be nem kapcsoltad a kamerat - ezt a feluleten ki is mondjuk.
   */
  const refreshDevices = useCallback(async (): Promise<Record<DeviceKind, Device[]> | null> => {
    if (!navigator.mediaDevices?.enumerateDevices) return null;
    try {
      const all = await navigator.mediaDevices.enumerateDevices();

      /*
        A NEV NELKULI bejegyzest kihagyjuk.

        Engedely nelkul a bongeszo egyetlen, URES azonositoju eszkozt ad
        vissza - es a `setSinkId('')` a rendszer alapertelmezesere kapcsol,
        vagyis tipikusan a gep hangszorojara. Egy ilyen sor tehat nemcsak
        haszontalan: pontosan azt a hibat okozza, amit a valaszto meg akar
        szuntetni.
      */
      const named = (kind: MediaDeviceKind) =>
        all
          .filter((device) => device.kind === kind && device.deviceId && device.label)
          .map((device) => ({ deviceId: device.deviceId, label: device.label }));

      /*
        A MIKROFONLISTA is itt frissul.

        Korabban csak a csatlakozaskori eloellenorzesbol jott, vagyis a hivas
        kozben bedugott fejhallgato mikrofonja meg sem jelent a listaban -
        pedig a fejhallgatot eppen a hivas kozben dugjak be a leggyakrabban.
      */
      /*
        Az UROS lista ketfele lehet: nincs eszkoz (kihuztak), vagy van, de NEV nelkul
        (nincs engedely). Az elobbinel a listanak ures kell lennie - kulonben a mar
        kihuzott mikrofon ott maradna, kijelolve; az utobbinal a regi lista marad.
      */
      const unlabeledInputs = all.some(
        (device) => device.kind === 'audioinput' && device.deviceId && !device.label,
      );
      const inputs = tidyDevices(named('audioinput'));
      if (inputs.length > 0 || !unlabeledInputs) setDevices(inputs);

      const outputs = canChooseOutput() ? tidyDevices(named('audiooutput')) : [];
      if (canChooseOutput()) setOutputDevices(outputs);

      const videos = named('videoinput');
      setVideoDevices(videos);

      /*
        A listakat VISSZA is adjuk.

        A megjegyzett valasztast a friss listahoz kell merni, es a React
        allapota csak a kovetkezo kirajzolasnal frissul - addigra a
        csatlakozas mar tul lenne azon a ponton, ahol az eszkozt at kell
        allitani.
      */
      return { audioinput: inputs, audiooutput: outputs, videoinput: videos };
    } catch {
      // Eszkozlista nelkul is megy a hivas - csak valasztani nem lehet.
      return null;
    }
  }, []);

  /**
   * Az eszkozok menet kozben is valtoznak.
   *
   * Fejhallgatot a hivas KOZBEN dugnak be a leggyakrabban - eppen akkor, amikor
   * a valtasra szukseg van. A lista enelkul a csatlakozaskori allapotot
   * mutatna, es az uj eszkoz meg sem jelenne benne.
   */
  useEffect(() => {
    if (!navigator.mediaDevices?.addEventListener) return;

    const onChange = () => void refreshDevices();
    navigator.mediaDevices.addEventListener('devicechange', onChange);
    return () => navigator.mediaDevices.removeEventListener('devicechange', onChange);
  }, [refreshDevices]);

  // ─── Eletjel ────────────────────────────────────────────────────────────────

  const sendHeartbeat = useCallback(
    async (
      flags: {
        isMuted?: boolean;
        isCameraOn?: boolean;
        isSharingScreen?: boolean;
        isPaused?: boolean;
      } = {},
    ) => {
      const current = huddleRef.current;
      if (!current) return;

      try {
        const fresh = await huddleHeartbeat(current.id, { sessionId: SESSION_ID, ...flags });
        setHuddle(fresh);
        upsertHuddle(queryClient, fresh);
      } catch (err) {
        // 409 = mar nem vagyunk bent (masik ful vette at, vagy lezarult).
        // Ez nem atmeneti hiba: itt bezarunk, nem probalkozunk tovabb.
        if (err instanceof ApiRequestError && err.status === 409) {
          teardown(err.message);
        }
      }
    },
    [queryClient, teardown],
  );

  const startHeartbeat = useCallback(() => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    heartbeatRef.current = setInterval(() => {
      if (!isLiveState(stateRef.current)) return;
      void sendHeartbeat();
    }, HUDDLE_HEARTBEAT_INTERVAL_MS);
  }, [sendHeartbeat]);

  // ─── Media-esemenyek ────────────────────────────────────────────────────────

  /**
   * Reakcio megjelenitese.
   *
   * Sajat magunknal is lefut (a kuldo is latja) - kulonben nem lenne
   * visszajelzes arrol, hogy elment.
   */
  const showReaction = useCallback((emoji: string, from: string) => {
    const id = `r-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setReactions((current) => [...current.slice(-20), { id, emoji, from, at: Date.now() }]);
    setTimeout(
      () => setReactions((current) => current.filter((item) => item.id !== id)),
      HUDDLE_REACTION_TTL_MS,
    );
  }, []);

  /**
   * Egy resztvevo kezenek atallitasa.
   *
   * A PITTY itt szol - mindenkinel, a jelentkezonel is (o ebbol tudja, hogy
   * tenyleg elment). Csak a felemeles szol, a leengedes nem: az nem kerdes,
   * hanem valasz. Szunetben nem szol, mert akkor a hivasbol semmit nem
   * hallunk.
   */
  const setVote = useCallback((identity: string, vote: QuickVote | null) => {
    const current = quickVotesRef.current;
    if ((current[identity] ?? null) === vote) return;
    const next = { ...current };
    if (vote) next[identity] = vote;
    else delete next[identity];
    quickVotesRef.current = next;
    setQuickVotes(next);
  }, []);

  const setHand = useCallback((identity: string, raisedAt: number) => {
    const current = handsRef.current;
    const before = current[identity] ?? 0;
    if (before === raisedAt) return;

    const next = { ...current };
    if (raisedAt > 0) next[identity] = raisedAt;
    else delete next[identity];
    handsRef.current = next;
    setRaisedHands(next);

    if (before === 0 && raisedAt > 0 && !pausedRef.current) playHandRaiseChime();
  }, []);

  const setAway = useCallback((identity: string, since: number) => {
    const current = awayRef.current;
    if ((current[identity] ?? 0) === since) return;
    const next = { ...current };
    if (since > 0) next[identity] = since;
    else delete next[identity];
    awayRef.current = next;
    setAwayIds(next);
  }, []);

  /**
   * A kamera KI-BE kapcsolasa a LiveKitben NEM sav-leallitas, hanem NEMITAS.
   *
   * Kikapcsolaskor a sav megmarad (nemitva), bekapcsolaskor ugyanaz a sav
   * oldodik fel - uj "sav indult" esemeny nincs. A csempe ezert nem a lista
   * tagsagabol, hanem a sav `isMuted` allapotabol tudja, mutasson-e kepet.
   * A nemitas-esemenyre csak ujrarajzoltatjuk a listat.
   *
   * Eles hiba volt: kikapcsolaskor kivettuk a sajat csempet, bekapcsolaskor
   * pedig semmi nem tette vissza - a tobbieknel kozben a nemitott sav csempeje
   * feketen allt.
   */
  const refreshCameraTiles = useCallback(() => {
    setCameraTracks((current) => [...current]);
  }, []);

  const attachRoomEvents = useCallback(
    (room: Room, huddleId: string) => {
      room
        .on(lk().RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
          setSpeakingIds(speakers.map((s) => s.identity));
        })
        /*
          A BONGESZO LETILTHATJA A HANGOT.

          Automatikus lejatszast a bongeszok felhasznaloi mozdulat nelkul nem
          engednek. Ilyenkor a hivas el, a resztvevok latszanak - es nem
          hallatszik semmi. Jelzes nelkul ez ugy nezne ki, mintha minden
          rendben lenne, es a felhasznalo a sajat hangszorojat keresgelne.
        */
        .on(lk().RoomEvent.AudioPlaybackStatusChanged, () => {
          setIsAudioBlocked(!room.canPlaybackAudio);
        })
        /**
         * Be- vagy kilepett valaki: azonnal friss resztvevo-listat kerunk.
         *
         * A LiveKit esemenye csak a JELZES - a listat tovabbra is a szerver
         * adja (ott dol el, ki van "hivjuk" es ki "bent" allapotban). Enelkul
         * a behivott csak a kovetkezo eletjelnel (20 mp) valtana at "bent"-re,
         * pedig eppen az a vizualis visszaigazolas a lenyeg.
         */
        .on(lk().RoomEvent.ParticipantConnected, () => {
          applyBreakoutFilter(room);
          void sendHeartbeat();
        })
        // Uj sav (kamera, megosztas, hang): a csoporton kivuliekrol azonnal leiratkozunk.
        .on(lk().RoomEvent.TrackPublished, () => applyBreakoutFilter(room))
        .on(lk().RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
          setHand(participant.identity, 0);
          setAway(participant.identity, 0);
          setVote(participant.identity, null);
          void sendHeartbeat();
        })
        .on(
          lk().RoomEvent.ParticipantAttributesChanged,
          (changed: Record<string, string>, participant: Participant) => {
            if (AWAY_ATTRIBUTE in changed) setAway(participant.identity, awayOf(participant));
            if (VOTE_ATTRIBUTE in changed) setVote(participant.identity, voteOf(participant));
            if (!(HAND_ATTRIBUTE in changed)) return;
            setHand(participant.identity, handOf(participant));
          },
        )
        /**
         * A SAJAT mikrofonunk allapota kivulrol is valtozhat.
         *
         * Ha valaki elnemit minket, azt a szerver a LiveKitnel vegzi el - a
         * gombunk pedig tovabbra is "bekapcsolva" allna, es ugy beszelnenk,
         * hogy senki nem hall. Ezert nem a sajat kattintasunkat tekintjuk
         * igazsagnak, hanem a sav tenyleges allapotat.
         */
        .on(lk().RoomEvent.TrackMuted, (pub: TrackPublication, participant: Participant) => {
          if (pub.source === lk().Track.Source.Camera) refreshCameraTiles();
          if (participant.identity !== room.localParticipant.identity) return;
          if (pub.source !== lk().Track.Source.Microphone) return;
          setIsMuted(true);
          void sendHeartbeat({ isMuted: true });
        })
        .on(lk().RoomEvent.TrackUnmuted, (pub: TrackPublication, participant: Participant) => {
          if (pub.source === lk().Track.Source.Camera) refreshCameraTiles();
          if (participant.identity !== room.localParticipant.identity) return;
          if (pub.source !== lk().Track.Source.Microphone) return;
          setIsMuted(false);
          // A sav ELO: a felvetel innen tudja, hogy ujra megprobalhatja.
          setMicGeneration((current) => current + 1);
          void sendHeartbeat({ isMuted: false });
        })
        .on(
          lk().RoomEvent.TrackSubscribed,
          (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
            if (track.kind === lk().Track.Kind.Audio) {
              // A telefonos hivo hangja: az atirata ebbol a savbol keszul.
              if (participant.identity.startsWith('sip_')) {
                setPhoneTrackGeneration((current) => current + 1);
                // A telefonvonal zaja (utca, autó, kihangosito) a hallgatonal szurodik.
                const wanted = noiseWantedRef.current;
                if (wanted !== 'off' && track instanceof lk().RemoteAudioTrack) {
                  const sid = track.sid ?? participant.identity;
                  void applyRemoteNoiseFilter(track, wanted).then((filter) => {
                    if (filter) phoneFiltersRef.current.set(sid, filter);
                  });
                }
              }

              // A hangot rejtett elemekre kotjuk. Ha ezt Reactre biznank, egy
              // ujrarajzolas leszakithatna a hangot valakirol.
              const element = track.attach();
              element.setAttribute('data-identity', participant.identity);

              /*
                Az elem nemitasat a LiveKit intezi (Web Audio uton szol a hang),
                ezert hozza NEM nyulunk. A szunet hangerovel csendesit - igy a
                KESOBB belepok hangja sem szolal meg, amig szunetelunk.

                Ugyanitt all helyre a SZEMELYENKENTI hangero is. Enelkul az,
                akit lehalkitottunk, ujra teljes hangeron szolalna meg, amint
                kiesik es visszajon a halozata - vagy ha csak most lep be,
                miutan korabban mar beallitottuk. Pontosan ugyanaz a csapda,
                mint a hangszoro-valasztasnal nehany sorral lentebb.
              */
              const volume = pausedRef.current
                ? 0
                : (volumesRef.current[participant.identity] ?? 1);
              if (volume !== 1) participant.setVolume(volume);

              /*
                A valasztott hangszorora kotjuk - a KESOBB belepoket is.

                Enelkul a beallitas csak azokra ervenyesulne, akik a valasztas
                pillanataban mar bent voltak; aki utana lep be, az megint a
                rendszer alapertelmezesen szolalna meg.
              */
              /*
                A valasztott hangszorora kotjuk - a KESOBB belepoket is.

                A savon is beallitjuk (`setSinkId`), nem csak ezen az egy
                elemen: igy a LiveKit minden tovabbi rakotesnel maga is ezt
                fogja hasznalni.
              */
              const output = outputIdRef.current;
              if (output) {
                // A savon is - igy a LiveKit minden tovabbi rakoteset ez vezerli.
                if (track instanceof lk().RemoteAudioTrack) {
                  void track.setSinkId(output).catch(() => undefined);
                }
                if ('setSinkId' in element) {
                  void (element as HTMLMediaElement & { setSinkId(id: string): Promise<void> })
                    .setSinkId(output)
                    .catch(() => undefined);
                }
              }

              audioRef.current?.appendChild(element);
              return;
            }
            if (track.source === lk().Track.Source.ScreenShare) {
              const entry = { identity: participant.identity, track };
              setScreenShares((current) => [
                ...current.filter((item) => item.identity !== participant.identity),
                entry,
              ]);
              /*
                Az ELSO megosztas magatol megjelenik, a tobbit a felhasznalo
                kapcsolja be. Igy nem rantja el senki a kepet: aki eppen egy
                megosztast nez, annak nem ugrik be melle egy masodik, amirol
                nem o dontott.
              */
              setVisibleShares((current) =>
                current.length > 0 ? current : [participant.identity],
              );
              return;
            }

            if (track.source === lk().Track.Source.Camera) {
              setCameraTracks((current) => [
                ...current.filter((item) => item.identity !== participant.identity),
                { identity: participant.identity, track },
              ]);
            }
          },
        )
        .on(
          lk().RoomEvent.TrackUnsubscribed,
          (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
            track.detach().forEach((element) => element.remove());
            if (track.kind === lk().Track.Kind.Audio && participant.identity.startsWith('sip_')) {
              setPhoneTrackGeneration((current) => current + 1);
              const sid = track.sid ?? participant.identity;
              phoneFiltersRef.current.get(sid)?.stop();
              phoneFiltersRef.current.delete(sid);
            }

            if (track.source === lk().Track.Source.ScreenShare) {
              setScreenShares((current) => {
                const next = current.filter((item) => item.track !== track);
                /*
                A megszunt megosztas lekerul a listarol.

                Ha ezzel URESSE valna, de meg van mit nezni, atvaltunk a
                megmaradtra - kulonben a nagy terulet ott maradna uresen, holott
                valaki eppen oszt meg valamit. Ez NEM mond ellent a kapcsolo
                logikanak: ha a felhasznalo kapcsolta ki mindet, oda nem nyulunk,
                csak akkor, ha a megosztas maga ert veget.
              */
                setVisibleShares((current) => {
                  const maradt = current.filter((id) => next.some((item) => item.identity === id));
                  if (maradt.length > 0) return maradt;
                  return next[0] ? [next[0].identity] : [];
                });
                return next;
              });
            }

            if (track.source === lk().Track.Source.Camera) {
              setCameraTracks((current) => current.filter((item) => item.track !== track));
            }
          },
        )
        /**
         * Mulandó adatok: reakciok es rajz.
         *
         * Ezek NEM mennek at a szerveren, es nem is kerulnek adatbazisba - a
         * LiveKit adatcsatornajan utaznak resztvevotol resztvevoig. Ami
         * elveszik, az elveszett: egy rajzvonal nem er annyit, hogy garantalt
         * kezbesitest epitsunk ra.
         */
        .on(lk().RoomEvent.DataReceived, (payload: Uint8Array, sender?: RemoteParticipant) => {
          let frame: HuddleDataFrame;
          try {
            const parsed = huddleDataFrameSchema.safeParse(
              JSON.parse(new TextDecoder().decode(payload)),
            );
            if (!parsed.success) return;
            frame = parsed.data;
            // Kiscsoportos idoszak alatt csak a sajat csoport jelzeseit fogadjuk (reakcio, kez, rajz, keres).
            if (
              parsed.data.type !== 'breakout.help' &&
              sender &&
              !sameBreakoutGroup(
                breakoutRef.current,
                room.localParticipant.identity,
                sender.identity,
              )
            ) {
              return;
            }
          } catch {
            return;
          }

          if (frame.type === 'breakout.help') {
            // Csak a hazigazdanak jon (a kuldo a cimzettet adja meg); a kuldot a LiveKit adja.
            if (sender && huddleRef.current?.hostId === room.localParticipant.identity) {
              const entry = { from: sender.identity, group: frame.group, at: Date.now() };
              setHelpRequests((current) => [
                ...current.filter((item) => item.from !== entry.from),
                entry,
              ]);
            }
          } else if (frame.type === 'request' || frame.type === 'request.answer') {
            // A kuldot a LiveKit kezbesitese adja, nem a keret: igy nem
            // lehet mas neveben kerni vagy valaszolni.
            if (sender) requestFrameRef.current(frame, sender.identity);
          } else if (frame.type === 'chat') {
            setChatSignal((current) => current + 1);
          } else if (frame.type === 'reaction') {
            showReaction(frame.emoji, frame.from);
          } else if (frame.type === 'vote.clear') {
            dropQuickVotesRef.current();
          } else if (frame.type === 'draw') {
            setStrokes((current) => [...current.slice(-300), frame]);
          } else {
            setStrokes((current) => current.filter((stroke) => stroke.target !== frame.target));
          }
        })
        /**
         * A SAJAT savjaink.
         *
         * A `TrackSubscribed` csak a tobbiek savjait hozza - amit mi kuldunk,
         * arra nem iratkozunk fel. Ha ezt nem kezelnenk kulon, a sajat
         * kameraképünk es a sajat megosztasunk sehol nem latszana nalunk.
         */
        .on(lk().RoomEvent.LocalTrackPublished, (pub: LocalTrackPublication) => {
          const track = pub.track;
          if (!track) return;
          const identity = room.localParticipant.identity;

          /*
            A MIKROFONSAV MOST JOTT LETRE.

            A felvetel csak ekkor tud elindulni; a nemitas feloldasa onmagaban
            meg nem eleg (lasd a felvetel hatasat).
          */
          if (pub.source === lk().Track.Source.Microphone) {
            setMicGeneration((current) => current + 1);

            /*
              ZAJSZURES az uj mikrofonsavra.

              Itt, es nem a belepesnel: a sav kesobb is letrejohet (pl. ha a
              belepeskor nem indult el a mikrofon, es csak a nemitas feloldasa
              nyitja meg). Az eszkozvaltas NEM hoz uj savot - a LiveKit a
              processzort atviszi az uj eszkozre.
            */
            const wanted = noiseWantedRef.current;
            if (wanted !== 'off') {
              void applyNoiseFilter(track as LocalAudioTrack, wanted).then(setNoiseLevelState);
            } else {
              setNoiseLevelState('off');
            }

            /*
              A KEZDO nemitas-allapotot itt kell atvennunk.

              A `TrackMuted` / `TrackUnmuted` esemenyek csak a KESOBBI
              valtozasokat kovetik - egy most letrejott savnal egyik sem sul
              el. Amig elo mikrofon nelkul leptunk be, ez nem szamitott: nem
              volt sav. Amiota elo mikrofonnal lepunk be, szamit - enelkul a
              gomb "nemitva"-t mutatna, kozben mindenki hall minket. Ez a
              legrosszabb fajta hiba: a felulet az ellenkezojet allitja annak,
              ami tortenik.
            */
            setIsMuted(pub.isMuted);
            void sendHeartbeat({ isMuted: pub.isMuted });
            return;
          }

          if (pub.source === lk().Track.Source.Camera) {
            setCameraTracks((current) => [
              ...current.filter((item) => item.identity !== identity),
              { identity, track },
            ]);
            return;
          }

          if (pub.source === lk().Track.Source.ScreenShare) {
            setIsSharingScreen(true);
            setScreenShares((current) => [
              ...current.filter((item) => item.identity !== identity),
              { identity, track },
            ]);
            /*
              A SAJAT megosztas azonnal megjelenik - feltetel nelkul.

              Aki most nyomta meg a megosztast, az eppen azt akarja ellenorizni,
              hogy a JO ablakot valasztotta-e. Amit eddig neztunk, az MARAD: a
              sajat kep mostantol melle kerul, nem helyette.
            */
            setVisibleShares((current) =>
              current.includes(identity) ? current : [...current, identity],
            );
          }
        })
        .on(lk().RoomEvent.LocalTrackUnpublished, (pub: LocalTrackPublication) => {
          const identity = room.localParticipant.identity;

          if (pub.source === lk().Track.Source.ScreenShare) {
            setIsSharingScreen(false);
            /*
              A KERET INNEN IS le kell kerulnie.

              A megosztast a bongeszo sajat "Megosztas leallitasa" savjarol is
              le lehet allitani - olyankor a mi gombunk kezelője le sem fut, es
              a keret ott maradna egy olyan kijelzo korul, amit mar senki nem
              lat. Egy hazudo jelzes rosszabb, mint a semmi.
            */
            setShareFrame('off');
            setShareMonitors([]);
            void hideShareFrame();
            setScreenShares((current) => {
              const next = current.filter((item) => item.identity !== identity);
              // Ugyanaz a szabaly, mint masok megosztasanal: a megszunt kep
              // lekerul, es ha ezzel semmi nem maradna a kepernyon, atvaltunk
              // arra, ami meg megy.
              setVisibleShares((lathato) => {
                const maradt = lathato.filter((id) => next.some((item) => item.identity === id));
                if (maradt.length > 0) return maradt;
                return next[0] ? [next[0].identity] : [];
              });
              return next;
            });
            void sendHeartbeat({ isSharingScreen: false });
          }

          if (pub.source === lk().Track.Source.Camera) {
            setIsCameraOn(false);
            setCameraTracks((current) => current.filter((item) => item.identity !== identity));
            void sendHeartbeat({ isCameraOn: false });
          }
        })
        .on(lk().RoomEvent.Reconnecting, () => {
          // NEM dobjuk ki a felhasznalot: a halozatvaltas nem kilepesi szandek.
          transition('reconnecting');
          void reportHuddleEvent(huddleId, { kind: 'reconnecting' }).catch(() => undefined);
        })
        .on(lk().RoomEvent.Reconnected, () => {
          transition('connected');
          void reportHuddleEvent(huddleId, { kind: 'reconnected' }).catch(() => undefined);
          applyBreakoutFilter(room);
          void sendHeartbeat();
        })
        .on(lk().RoomEvent.Disconnected, () => {
          // Ha mar kifele tartottunk, ez a vart vegkifejlet.
          if (stateRef.current === 'leaving' || stateRef.current === 'idle') return;
          void reportHuddleEvent(huddleId, { kind: 'dropped' }).catch(() => undefined);
          teardown('A kapcsolat megszakadt a huddle-lel.');
        })
        .on(lk().RoomEvent.MediaDevicesError, (err: Error, kind?: MediaDeviceKind) => {
          setError(describeMediaError(err, sourceFromKind(kind, err.message)));
        })
        /*
          A LIVEKIT MAGATOL IS VALT ESZKOZT: kihuztak a fejhallgatot, megvaltozott a
          rendszer alapertelmezettje, vagy a track elhalt es ujraindult. A felulet
          kijelolese ezt eddig NEM kovette - a hivas az egyik mikrofonon ment, a lista
          egy masikat mutatott kijelolve. Innentol a valasztas a TENYLEGES allapotot
          tukrozi. (A hangszorot mi magunk kezeljuk - lasd switchOutput.)
        */
        .on(lk().RoomEvent.ActiveDeviceChanged, (kind: MediaDeviceKind, deviceId: string) => {
          if (kind === 'audioinput') setActiveDeviceId(deviceId);
          else if (kind === 'videoinput') setActiveCameraId(deviceId);
        });
    },
    [
      applyBreakoutFilter,
      refreshCameraTiles,
      sendHeartbeat,
      setAway,
      setHand,
      setVote,
      showReaction,
      teardown,
      transition,
    ],
  );

  /**
   * A hangszoro atallitasa - MI vegezzuk, nem a media-konyvtar.
   *
   * Elsore a LiveKit `switchActiveDevice` hivasara biztuk, es a valasztas
   * NEM ERVENYESULT: a hang tovabbra is a laptop hangszorojan szolt. A
   * konyvtar sajat nyilvantartasa nem fedte le azokat az elemeket, amiket mi
   * magunk kotunk fel (`track.attach()` + sajat tarolo).
   *
   * Ezert most magunk hivjuk a `setSinkId`-t MINDEN elemre, es a valasztast
   * megjegyezzuk a kesobb belepokre is. A LiveKit hivasa megmarad - hadd
   * legyen rendben a sajat konyvelese -, de a mukodes nem tole fugg.
   */
  /**
   * A hangkimenet atallitasa - A SZOBA savjain, nem a sajat DOM-unkon.
   *
   * Elsore a sajat, rejtett tarolonk gyerekein hivtuk a `setSinkId`-t. Ez ket
   * feltevesre epult, es MINDKETTO teved:
   *
   *  1. hogy minden lejatszo elem ott van a tarolonkban (a `track.attach()`
   *     akkor is letrehozza es elinditja az elemet, ha nem tesszuk a DOM-ba);
   *  2. hogy a LiveKit nem tud rolunk - pedig a `track.attach()` eppen hogy
   *     BEJEGYZI az elemet a sav `attachedElements` listajaba.
   *
   * A sav listaja tehat a hiteles forras: ott minden lejatszo elem szerepel,
   * fuggetlenul attol, hova kerult a DOM-ban. Ezert most azon megyunk vegig -
   * es vissza is olvassuk, hogy tenyleg atallt-e.
   */
  /**
   * A hangkimenet beallitasa a lejatszo elemeken - HALONAK.
   *
   * A tenyleges valtast a LiveKit vegzi (Web Audio uton, lasd a szoba
   * beallitasait); ez a kor csak arra az esetre marad, ha valamiert megis
   * elemszinten szolna a hang.
   *
   * A sav `attachedElements` listaja a forras: a `track.attach()` oda jegyzi
   * be az elemet, fuggetlenul attol, hova kerult a DOM-ban.
   */
  const applyOutput = useCallback(async (deviceId: string): Promise<void> => {
    const room = roomRef.current;
    const elements = new Set<HTMLMediaElement>();

    for (const participant of room?.remoteParticipants.values() ?? []) {
      for (const publication of participant.audioTrackPublications.values()) {
        for (const element of publication.track?.attachedElements ?? []) {
          if (element instanceof HTMLMediaElement) elements.add(element);
        }
      }
    }

    for (const element of audioRef.current?.children ?? []) {
      if (element instanceof HTMLMediaElement) elements.add(element);
    }

    for (const element of elements) {
      if (!('setSinkId' in element)) continue;
      await (element as HTMLMediaElement & { setSinkId(id: string): Promise<void> }).setSinkId(
        deviceId,
      );
    }
  }, []);

  /**
   * A megjegyzett eszkozok visszaallitasa csatlakozaskor.
   *
   * **Csendben.** Ha a tegnapi fejhallgato ma nincs bedugva, az nem hiba: a
   * `resolveDevice` ilyenkor `null`-t ad, es marad a bongeszo
   * alapertelmezese. Hibauzenetet a hivas INDULASAKOR nem mutatunk azert,
   * mert kihuztad a fuleset.
   *
   * A kamerat csak BEALLITJUK, nem kapcsoljuk be: a huddle kamera nelkul
   * indul, es ezen a valasztas nem valtoztathat.
   */
  const restoreDevices = useCallback(
    async (room: Room, lists: Record<DeviceKind, Device[]>) => {
      const wanted = (kind: DeviceKind) => resolveDevice(rememberedDevice(kind), lists[kind]);

      /*
        A kijelolest CSAK SIKER UTAN allitjuk at. Korabban a valtas eredmenyetol
        fuggetlenul a megjegyzett eszkoz latszott kijelolve - akkor is, ha a hang
        kozben a regi mikrofonon maradt (foglalt eszkoz, hiba).

        A mikrofonnal nincs mit valtani, ha a belepeskor mar ezzel inditottunk
        (lasd audioCaptureDefaults a join-ban): a masodik megnyitas csak
        felesleges kattogas es versenyhelyzet lenne.
      */
      const mic = wanted('audioinput');
      if (mic && room.getActiveDevice('audioinput') !== mic) {
        const switched = await room.switchActiveDevice('audioinput', mic).catch(() => false);
        if (switched) setActiveDeviceId(mic);
      }

      const camera = wanted('videoinput');
      if (camera) {
        const switched = await room.switchActiveDevice('videoinput', camera).catch(() => false);
        if (switched) setActiveCameraId(camera);
      }

      const output = wanted('audiooutput');
      if (output && canChooseOutput()) {
        // ELOSZOR a ref: a kesobb belepok rakotese ebbol olvas.
        outputIdRef.current = output;
        setActiveOutputId(output);
        await room.switchActiveDevice('audiooutput', output).catch(() => undefined);
        await applyOutput(output).catch(() => undefined);
      }
    },
    [applyOutput],
  );

  // ─── Csatlakozas ────────────────────────────────────────────────────────────

  const join = useCallback(
    async (
      targetChannelId: string,
      title?: string,
      options?: { answer?: boolean; quiet?: boolean },
    ) => {
      if (stateRef.current !== 'idle' && stateRef.current !== 'failed') return;

      setError(null);
      setPreflight(null);
      if (!transition('preflight')) return;
      setChannelId(targetChannelId);
      joinStartedAt.current = Date.now();
      const ticket: PendingJoin = { huddleId: null, outcome: null };
      pendingJoinRef.current = ticket;

      // 1. Eloellenorzes - itt derul ki idoben, ha valami hianyzik.
      const checks = await runPreflight();
      setPreflight(checks);
      setDevices(checks.inputDevices);

      if (!checks.ok) {
        transition('failed');
        const first = checks.checks.find((c) => !c.ok);
        setError(
          // A szerver sem erheto el: nincs kapcsolat - a hivott kesobb ertesul (K2).
          checks.unreachable && !options?.answer && !options?.quiet
            ? offlineCallMessage(queryClient, targetChannelId)
            : (first?.detail ?? 'Az előellenőrzés nem sikerült.'),
        );
        setChannelId(null);
        void reportPreflightFailure(first?.detail?.slice(0, 200)).catch(() => undefined);
        return;
      }

      if (!transition('connecting')) return;

      /*
        A media-szerver cime a hibauzenetbe is bekerul.

        Enelkul a "could not establish signal connection" onmagaban nem mond
        semmit: nem derul ki belole, HOVA nem sikerult csatlakozni. A cim az
        elso dolog, amit egy ilyen hibanal meg kell nezni.
      */
      let mediaUrl: string | null = null;

      try {
        // 2. Szerver: huddle inditasa vagy csatlakozas + rovid eletu jegy.
        const { huddle: fresh, token } = await startHuddle(targetChannelId, {
          sessionId: SESSION_ID,
          ...(title ? { title } : {}),
          ...(options?.answer ? { answer: true } : {}),
          ...(options?.quiet ? { quiet: true } : {}),
        });

        ticket.huddleId = fresh.id;
        if (ticket.outcome) {
          /*
            Mire a szerver valaszolt, a felhasznalo mar kilepett (bontott,
            Esc). A szerver ekkor mar beirt minket - a probalkozast is, a
            resztvevot is -, ezert mindkettot rendbe tesszuk, es NEM
            csatlakozunk a szobahoz.
          */
          void reportHuddleEvent(fresh.id, {
            kind: 'join_cancelled',
            detail: 'Kilépés csatlakozás közben',
          }).catch(() => undefined);
          void leaveHuddleApi(fresh.id, SESSION_ID).catch(() => undefined);
          return;
        }

        setHuddle(fresh);
        huddleRef.current = fresh;
        upsertHuddle(queryClient, fresh);

        // A tobbi fulnek szolunk, hogy ez a ful vette at.
        broadcastRef.current?.postMessage({
          type: 'claim',
          sessionId: SESSION_ID,
          huddleId: fresh.id,
        } satisfies BroadcastClaim);

        // 3. Media.
        const preferredMic = resolveDevice(rememberedDevice('audioinput'), checks.inputDevices);
        const room = new (await loadLivekit()).Room({
          adaptiveStream: true,
          dynacast: true,
          /*
            ZAJSZURES - a bongeszo sajat feldolgozasaval.

            Nem diszites: egy nyitott iroda, egy billentyuzet vagy egy
            ventilator hangja a beszedet is elnyomja, es az atiratot is
            hasznalhatatlanna teszi. Ez a harom kapcsolo minden mai bongeszoben
            ott van, es kifejezetten bekapcsoljuk oket - a LiveKit
            alapertelmezesere nem hagyatkozunk, mert az verzionkent valtozhat.

            Nagyobb zajra ez sem eleg; ott mar kulon zajszuro modell kellene,
            ami sajat processzor-koltseggel jar. Ez az elso, olcso lepes.
          */
          audioCaptureDefaults: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            /*
              A MEGJEGYZETT MIKROFON mar az ELSO megnyitasnal ervenyes.

              Korabban a mikrofon a bongeszo alapertelmezett eszkozen indult, es csak
              BELEPES UTAN valtott a megjegyzettre: ket megnyitas, kozben rovid ideig a
              rossz mikrofon, es ha a valtas elbukott, a hang a rossz eszkozon maradt.
              Az azonosito a belepes elotti eloellenorzes listajabol jon (
esolveDevice),
              tehat csak olyan eszkoz kerul ide, ami tenyleg ott van - nem exact, igy
              hiba eseten a bongeszo az alapertelmezettre esik vissza.
            */
            ...(preferredMic ? { deviceId: preferredMic } : {}),
          },
          /*
            A TAVOLI HANG WEB AUDIO UTON MEGY - ezen mulik a hangszoro-valasztas.

            Elemszinten a `setSinkId` hiaba fut le sikeresen: a Chrome a WebRTC
            hangjat a visszhangszures miatt az ALAPERTELMEZETT eszkozre koti, es
            az elem beallitasat figyelmen kivul hagyja. Kimerve: a felulet
            "atallitva (1/1 hangforras)"-t jelentett - visszaolvasott
            `sinkId`-vel! -, a hang megis a laptop hangszorojan szolt tovabb.
            Ugyanerre a Chromium-hibara hivatkozik a LiveKit sajat forrasa is.

            Igy viszont a hang egy `AudioContext`-en at szol, es a kimenetet
            azon allitjuk at - azt a Chrome tiszteletben tartja.

            Ket kovetkezmenye van, es mindketto kezelve van lentebb:
             - a lejatszo elemeket a LiveKit maga nemitja el (a hang a
               grafikonrol jon), ezert az `element.muted`-hez NEM nyulunk;
             - a szunet ezert hangeronel (`setVolume`) csendesit, nem nemitassal.
          */
          webAudioMix: true,
          /*
            A SAJAT TURN-unk, ha be van allitva.

            A szerver nevvel adja at (`turn:avslack…:3478`), amit a megosztott
            DNS mindket oldalrol a helyes cimre old fel - ettol mukodik a hivas
            a ceges halozaton BELULROL es KIVULROL is.

            Ures lista eseten NEM adjuk at: a LiveKit ilyenkor a sajat listajat
            hasznalja, vagyis minden marad a regiben. Egy felig beallitott TURN
            nem ronthatja el azt, ami eddig mukodott.
          */
          ...(token.iceServers.length > 0 ? { rtcConfig: { iceServers: token.iceServers } } : {}),
          // A halozatvaltas legyen tulelheto: inkabb varjunk, mint dobjuk ki.
          reconnectPolicy: {
            nextRetryDelayInMs: (context) =>
              context.retryCount > 8 ? null : Math.min(1000 * 2 ** context.retryCount, 10_000),
          },
        });
        roomRef.current = room;
        attachRoomEvents(room, fresh.id);

        mediaUrl = token.url;
        // Kiscsoportos idoszak kozben csatlakozva NEM iratkozunk fel mindenre: a szuro szol,
        // kiket hallunk - igy egy pillanatra sem halljuk a tobbi csoportot.
        await room.connect(token.url, token.token, { autoSubscribe: !fresh.breakout });
        applyBreakoutFilter(room);

        /*
          Akik MAR jelentkeztek, mielott beleptunk.

          A valtozas-esemeny csak a kesobbieket hozza. Pitty nelkul: ezek nem
          most tortentek, egy belepeskori hangzavar csak megzavarna.
        */
        const earlier: Record<string, number> = {};
        const earlierAway: Record<string, number> = {};
        const earlierVotes: Record<string, QuickVote> = {};
        for (const participant of room.remoteParticipants.values()) {
          const raisedAt = handOf(participant);
          if (raisedAt > 0) earlier[participant.identity] = raisedAt;
          const vote = voteOf(participant);
          if (vote) earlierVotes[participant.identity] = vote;
          const awaySince = awayOf(participant);
          if (awaySince > 0) earlierAway[participant.identity] = awaySince;
        }
        handsRef.current = earlier;
        setRaisedHands(earlier);
        quickVotesRef.current = earlierVotes;
        setQuickVotes(earlierVotes);
        awayRef.current = earlierAway;
        setAwayIds(earlierAway);
        /*
          ELO MIKROFONNAL lepunk be.

          Korabban nemitva indultunk, "senki ne kerulhessen ugy hivasba, hogy
          nem tudja" alapon. A gyakorlatban ez forditva sult el: az ember
          elkezdte a mondatot, es csak a vegen derult ki, hogy nem hallotta
          senki. A belepes amugy is szandekos mozdulat - gomb, eszkoz-
          ellenorzes -, tehat nem er meglepeteskent, hogy elo a mikrofon.

          Ha a bekapcsolas nem sikerul (elvett engedely, foglalt eszkoz),
          NEMITVA maradunk: a hivasbol nem esunk ki emiatt, a mikrofongomb
          pedig - a sav tenyleges allapotat kovetve - helyesen mutatja majd,
          hogy nemitva vagyunk.
        */
        await room.localParticipant.setMicrophoneEnabled(true).catch((err: unknown) => {
          console.warn('[huddle] a mikrofon nem indult el, nemitva lepunk be', err);
        });

        setActiveDeviceId(room.getActiveDevice('audioinput') ?? null);

        /*
          A MEGJEGYZETT eszkozok visszaallitasa.

          Elobb friss listat kerunk (a nevek csak a most megadott engedely
          utan latszanak), es a mentett valasztast AHHOZ merjuk. Ami nincs
          meg, arra nem eroltetunk semmit - lasd `huddle/devices.ts`.
        */
        void refreshDevices().then((lists) => {
          if (lists) return restoreDevices(room, lists);
          return undefined;
        });

        /*
          A hangkimenethez NEM NYULUNK, amig a felhasznalo nem valaszt.

          Elsore itt beallitottuk a `'default'` eszkozt, hogy a listaban
          latszodjon, mi az aktualis - es ezzel ELRONTOTTUK a hangot: a
          `setSinkId('default')` a RENDSZER alapertelmezesere kenyszeriti a
          lejatszast, vagyis a laptop hangszorojara. Aki addig fejhallgatoban
          hallgatta a hivast (mert a bongeszo oda iranyitotta), annak a hang
          atvandorolt a gepbe.

          A `setSinkId` egyiranyu: ha egyszer beallitottuk, mar nem lehet
          "vissza a bongeszore" bizni. Ezert amig nincs valasztas, hozza sem
          nyulunk.
        */

        if (!transition('connected')) {
          // Kozben kileptunk - a szoba mar nem kell.
          teardown(null);
          return;
        }

        startHeartbeat();
        settleJoin(ticket, 'join_success', undefined, Date.now() - joinStartedAt.current);
        if (pendingJoinRef.current === ticket) pendingJoinRef.current = null;
        void queryClient.invalidateQueries({ queryKey: huddlesKey });
      } catch (err) {
        if (ticket.outcome) {
          /*
            Kozben mar eldolt: a felhasznalo kilepett, vagy egy masik ful vette
            at - a szoba ezert bukott el. Ez nem csatlakozasi hiba, es a
            lebontas mar megtortent; hibauzenetet sem irunk ra.
          */
          return;
        }
        const where = mediaUrl ? ` (média-szerver: ${mediaUrl})` : '';
        let message =
          err instanceof ApiRequestError
            ? err.message
            : err instanceof Error
              ? `Nem sikerült csatlakozni: ${err.message}${where}`
              : `Nem sikerült csatlakozni az öleléshez.${where}`;

        const current = huddleRef.current;

        // NINCS KAPCSOLAT, a hivas el sem indult (K2) - lasd `offlineCallMessage`.
        if (!current && !options?.answer && !options?.quiet && isUnreachable(err)) {
          message = offlineCallMessage(queryClient, targetChannelId);
        }
        settleJoin(ticket, 'join_failed', message.slice(0, 200));
        if (current) {
          /*
            A SZERVERNEK IS szolunk, hogy kileptunk.

            A `startHuddle` mar bejegyzett minket resztvevokent - az meg a
            media elott lefut. Ha a media utana elhasal, es csak helyben
            takaritanank, a szerver szerint bent maradnank a szivveres
            lejartaig: a sajat fo ablakunk "Olelesben vagy"-t mutatna, a
            tobbieknek pedig ugy latszanank, mintha bent ulnenk egy hivasban,
            amibe be sem jutottunk.
          */
          void leaveHuddleApi(current.id, SESSION_ID).catch(() => undefined);
        }

        // A `teardown` `idle`-be visz es kiirja a hibat. Nem hagyjuk a
        // felhasznalot fel-csatlakozott allapotban ragadni.
        teardown(message);
      }
    },
    [
      applyBreakoutFilter,
      attachRoomEvents,
      queryClient,
      refreshDevices,
      restoreDevices,
      startHeartbeat,
      teardown,
      transition,
    ],
  );

  // ─── Vezerlok ───────────────────────────────────────────────────────────────

  const toggleMute = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;

    const next = !isMuted;
    setIsMuted(next);
    /*
      Aki visszakapcsolja a mikrofont, az visszajott: beszelni kezd, es nem
      lenne ertelme kulon a "mindjart jovok"-ot is levennie.
    */
    const identity = room.localParticipant.identity;
    if (!next && awayRef.current[identity]) {
      setAway(identity, 0);
      void room.localParticipant.setAttributes({ [AWAY_ATTRIBUTE]: '' }).catch(() => undefined);
    }
    void room.localParticipant.setMicrophoneEnabled(!next).catch((err: Error) => {
      setIsMuted(!next);
      setError(`A mikrofon állítása nem sikerült: ${err.message}`);
    });
    void sendHeartbeat({ isMuted: next });
  }, [isMuted, sendHeartbeat, setAway]);

  /** Adatkuldes a hivas resztvevoinek. Sosem dob: mulandó adat. */
  const publishData = useCallback((frame: HuddleDataFrame, reliable = false) => {
    const room = roomRef.current;
    if (!room) return;
    try {
      void room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(frame)), {
        // A rajz es a reakcio inkabb legyen GYORS, mint teljes: egy kimarado
        // vonalszakasz nem er egy akadast. A chat-jelzes viszont megbizhato
        // modon megy - ott egy elveszett keret kesobb erkezo uzenetet jelentene.
        reliable,
      });
    } catch {
      // lasd fent
    }
  }, []);

  /**
   * Szolunk a tobbieknek, hogy uj chat-uzenet van.
   *
   * Csak JELZES megy at, tartalom nem: a szoveget mindenki a szervertol kapja.
   * Igy egy resztvevo nem irathat a panelbe mas neveben.
   */
  const notifyChat = useCallback(() => {
    const from = roomRef.current?.localParticipant.identity ?? '';
    publishData({ type: 'chat', from }, true);
  }, [publishData]);

  /** Segitsegkeres a hazigazdatol - csak neki megy, megbizhatoan. */
  const requestBreakoutHelp = useCallback(() => {
    const room = roomRef.current;
    const hostId = huddleRef.current?.hostId;
    if (!room || !hostId || hostId === room.localParticipant.identity) return;
    const frame: HuddleDataFrame = {
      type: 'breakout.help',
      from: room.localParticipant.identity,
      group: breakoutGroupOf(breakoutRef.current, room.localParticipant.identity),
    };
    void room.localParticipant
      .publishData(new TextEncoder().encode(JSON.stringify(frame)), {
        reliable: true,
        destinationIdentities: [hostId],
      })
      .catch(() => undefined);
  }, []);

  const dismissHelp = useCallback(
    (from: string) => setHelpRequests((current) => current.filter((item) => item.from !== from)),
    [],
  );

  const sendReaction = useCallback(
    (emoji: string) => {
      const me = huddleRef.current;
      if (!me) return;
      const from = roomRef.current?.localParticipant.identity ?? '';
      publishData({ type: 'reaction', emoji, from });
      showReaction(emoji, from);
    },
    [publishData, showReaction],
  );

  /** Rovid visszajelzes a kereseinkrol - magatol eltunik. */
  const showNotice = useCallback((text: string) => {
    if (noticeTimerRef.current !== null) window.clearTimeout(noticeTimerRef.current);
    setRequestNotice(text);
    noticeTimerRef.current = window.setTimeout(() => setRequestNotice(null), REQUEST_NOTICE_MS);
  }, []);

  const nameFor = useCallback(
    (identity: string): string =>
      huddleRef.current?.participants.find((p) => p.user.id === identity)?.user.displayName ??
      'A kolléga',
    [],
  );

  const requestFrom = useCallback(
    (userId: string, kind: HuddleRequestKind) => {
      const room = roomRef.current;
      if (!room || pausedRef.current) return;

      const id = `q-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
      const frame: HuddleDataFrame = { type: 'request', id, kind };
      void room.localParticipant
        .publishData(new TextEncoder().encode(JSON.stringify(frame)), {
          // Egy elveszett keres csendes kudarc lenne: megbizhatoan megy.
          reliable: true,
          destinationIdentities: [userId],
        })
        .catch(() => undefined);

      /*
        Ha nem jon valasz, azt is kimondjuk.

        A cimzett ablaka lehet a hattereben, vagy epp most szakadt le. Enelkul
        a kero csak var, es nem tudja, erdemes-e ujra kerni.
      */
      const timer = window.setTimeout(() => {
        if (!outgoingRef.current.delete(id)) return;
        showNotice(`${nameFor(userId)} nem válaszolt a kérésre.`);
      }, REQUEST_TTL_MS + 5_000);
      outgoingRef.current.set(id, { to: userId, kind, timer });

      showNotice(
        kind === 'screen'
          ? `Megkértük ${nameFor(userId)} kollégát, hogy ossza meg a képernyőjét.`
          : `Megkértük ${nameFor(userId)} kollégát, hogy kapcsolja vissza a mikrofonját.`,
      );
    },
    [nameFor, showNotice],
  );

  const sendAnswer = useCallback(
    (to: string, id: string, answer: 'accepted' | 'declined' | 'unsupported' | 'expired') => {
      const room = roomRef.current;
      if (!room) return;
      const frame: HuddleDataFrame = { type: 'request.answer', id, answer };
      void room.localParticipant
        .publishData(new TextEncoder().encode(JSON.stringify(frame)), {
          reliable: true,
          destinationIdentities: [to],
        })
        .catch(() => undefined);
    },
    [],
  );

  /**
   * Bejovo keres vagy valasz - a szobaesemeny-kezelo hivja, refen at, mert az
   * a csatlakozaskor egyszer kotodik be, es a friss allapotot kell latnia.
   */
  const handleRequestFrame = useCallback(
    (frame: Extract<HuddleDataFrame, { type: 'request' | 'request.answer' }>, from: string) => {
      if (frame.type === 'request.answer') {
        const pending = outgoingRef.current.get(frame.id);
        if (!pending || pending.to !== from) return;
        window.clearTimeout(pending.timer);
        outgoingRef.current.delete(frame.id);

        const who = nameFor(from);
        showNotice(
          frame.answer === 'accepted'
            ? pending.kind === 'screen'
              ? `${who} elfogadta — indul a megosztás.`
              : `${who} visszakapcsolta a mikrofonját.`
            : frame.answer === 'unsupported'
              ? `${who} ezen az eszközön nem tud képernyőt megosztani.`
              : frame.answer === 'expired'
                ? `${who} nem válaszolt a kérésre.`
                : `${who} most nem szeretné.`,
        );
        return;
      }

      // Amit mar teljesitunk, arra nem kell kerdezni.
      if (
        frame.kind === 'screen' &&
        (roomRef.current?.localParticipant.isScreenShareEnabled ?? false)
      ) {
        sendAnswer(from, frame.id, 'accepted');
        return;
      }
      if (
        frame.kind === 'unmute' &&
        (roomRef.current?.localParticipant.isMicrophoneEnabled ?? false)
      ) {
        sendAnswer(from, frame.id, 'accepted');
        return;
      }
      // Telefonos bongeszoben nincs kepernyomegosztas - ne kerdezzuk feleslegesen.
      if (frame.kind === 'screen' && !canShareScreen()) {
        sendAnswer(from, frame.id, 'unsupported');
        return;
      }
      // Szuneten nem vagyunk itt: a kerest nem latnank, tehat nem is fogadjuk el.
      if (pausedRef.current) {
        sendAnswer(from, frame.id, 'declined');
        return;
      }

      /*
        Egyszerre EGY keres.

        Ha mar all egy kerdes, a regit lejartnak jelezzuk: ket egymasra nyilo
        ablak kozul az ember nem tudna, melyikre mond igent.
      */
      setIncomingRequest((current) => {
        if (current) sendAnswer(current.from, current.id, 'expired');
        return { id: frame.id, kind: frame.kind, from };
      });

      if (incomingTimerRef.current !== null) window.clearTimeout(incomingTimerRef.current);
      incomingTimerRef.current = window.setTimeout(() => {
        setIncomingRequest((current) => {
          if (current?.id === frame.id) {
            sendAnswer(current.from, current.id, 'expired');
            return null;
          }
          return current;
        });
      }, REQUEST_TTL_MS);
    },
    [nameFor, sendAnswer, showNotice],
  );
  const requestFrameRef = useRef(handleRequestFrame);
  requestFrameRef.current = handleRequestFrame;

  const toggleHand = useCallback(() => {
    const room = roomRef.current;
    if (!room || pausedRef.current) return;

    const identity = room.localParticipant.identity;
    const before = handsRef.current[identity] ?? 0;
    const raisedAt = before > 0 ? 0 : Date.now();

    // Azonnal mutatjuk (es szol a pitty): a visszaigazolas nem varhat a szerverre.
    setHand(identity, raisedAt);
    room.localParticipant
      .setAttributes({ [HAND_ATTRIBUTE]: raisedAt > 0 ? String(raisedAt) : '' })
      .catch((err: Error) => {
        setHand(identity, before);
        setError(`A jelentkezés nem ment át: ${err.message}`);
      });
  }, [setHand]);

  /**
   * GYORSSZAVAZAS - pipa vagy X egy szobeli kerdesre.
   *
   * Ugyanarra ujra nyomva visszavonja, a masikra nyomva atvalt. Azonnal
   * mutatjuk; ha a szerver nem fogadja, visszaallunk.
   */
  const castQuickVote = useCallback(
    (vote: QuickVote) => {
      const room = roomRef.current;
      if (!room) return;
      const identity = room.localParticipant.identity;
      const before = quickVotesRef.current[identity] ?? null;
      const next = before === vote ? null : vote;

      setVote(identity, next);
      room.localParticipant.setAttributes({ [VOTE_ATTRIBUTE]: next ?? '' }).catch((err: Error) => {
        setVote(identity, before);
        setError(`A szavazat nem ment át: ${err.message}`);
      });
    },
    [setVote],
  );

  /**
   * A gyorsszavazas TORLESE nalunk: a sajat szavazatot a resztvevo-adatbol is,
   * a tobbieket csak a kijelzesbol (az o adatukat ok maguk torlik).
   */
  const dropQuickVotes = useCallback(() => {
    const room = roomRef.current;
    const identity = room?.localParticipant.identity;
    if (room && identity && quickVotesRef.current[identity]) {
      void room.localParticipant.setAttributes({ [VOTE_ATTRIBUTE]: '' }).catch(() => undefined);
    }
    quickVotesRef.current = {};
    setQuickVotes({});
  }, []);
  const dropQuickVotesRef = useRef(dropQuickVotes);
  dropQuickVotesRef.current = dropQuickVotes;

  /** Nullazas MINDENKINEL: megbizhato csatornan szolunk, aztan nalunk is torlunk. */
  const clearQuickVotes = useCallback(() => {
    const from = roomRef.current?.localParticipant.identity ?? '';
    publishData({ type: 'vote.clear', from }, true);
    dropQuickVotes();
  }, [publishData, dropQuickVotes]);

  /*
    MAGATOL ELTUNIK, ha QUICK_VOTE_IDLE_MS ideig senki nem szavaz.

    Mindenki a sajat oraja szerint szamol, de ugyanazokat a valtozasokat latja
    - igy nagyjabol egyszerre tunik el mindenkinel, kozos ora nelkul. A
    kovetkezo szobeli kerdes igy tiszta lappal indul.
  */
  useEffect(() => {
    if (Object.keys(quickVotes).length === 0) return;
    const timer = window.setTimeout(dropQuickVotes, QUICK_VOTE_IDLE_MS);
    return () => window.clearTimeout(timer);
  }, [quickVotes, dropQuickVotes]);

  /**
   * MINDJART JOVOK - pl. kiugrok egy percre.
   *
   * Tavozaskor a mikrofon elnemul es a kez leereszkedik: az ures szek elotti
   * nyitott mikrofon csak zajt kozvetit, a fent hagyott kez pedig olyasvalakit
   * jelolne, aki nem tud szolni.
   *
   * Visszaterve a mikrofon olyan lesz, amilyen a tavozas ELOTT volt: aki nyitott
   * mikrofonnal ment el, annak visszakapcsol, aki eleve nemitva ult, az nemitva
   * marad. (Eredetileg sosem kapcsolt vissza - a gyakorlatban mindenki a
   * bogrevel akart visszajonni, es utana nemitva kezdett beszelni.)
   * A mikrofon-gombbal is vissza lehet jonni, lasd toggleMute.
   */
  const toggleAway = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;

    const identity = room.localParticipant.identity;
    const before = awayRef.current[identity] ?? 0;
    const since = before > 0 ? 0 : Date.now();

    setAway(identity, since);
    const attributes: Record<string, string> = { [AWAY_ATTRIBUTE]: since > 0 ? String(since) : '' };
    if (since > 0 && handsRef.current[identity]) {
      setHand(identity, 0);
      attributes[HAND_ATTRIBUTE] = '';
    }
    room.localParticipant.setAttributes(attributes).catch((err: Error) => {
      setAway(identity, before);
      setError(`Az állapot nem ment át: ${err.message}`);
    });

    if (since > 0) {
      micBeforeAwayRef.current = !isMuted;
      if (!isMuted) {
        setIsMuted(true);
        void room.localParticipant.setMicrophoneEnabled(false).catch(() => undefined);
        void sendHeartbeat({ isMuted: true });
      }
      return;
    }

    // Szuneten a mikrofont a szerver is lezarta - ott a visszakapcsolas hazudna.
    const restore = micBeforeAwayRef.current && isMuted && !pausedRef.current;
    micBeforeAwayRef.current = false;
    if (restore) {
      setIsMuted(false);
      void room.localParticipant.setMicrophoneEnabled(true).catch((err: Error) => {
        setIsMuted(true);
        setError(`A mikrofon visszakapcsolása nem sikerült: ${err.message}`);
      });
      void sendHeartbeat({ isMuted: false });
    }
  }, [isMuted, sendHeartbeat, setAway, setHand]);

  /*
    SZUNETRE MENVE a kez leereszkedik.

    Aki szuneten van, az nem hallja, ha szolitjak - egy fent hagyott kez
    olyasvalakit jelolne, aki eppen nem tud szolni.
  */
  useEffect(() => {
    const room = roomRef.current;
    if (!isPaused || !room) return;
    const identity = room.localParticipant.identity;
    if (!handsRef.current[identity]) return;

    setHand(identity, 0);
    void room.localParticipant.setAttributes({ [HAND_ATTRIBUTE]: '' }).catch(() => undefined);
  }, [isPaused, setHand]);

  /**
   * Gyorsgombok hivas kozben:
   * - Ctrl+H - jelentkezes / leengedes
   * - Ctrl+M - nemitas / nemitas feloldasa
   *
   * A bongeszoben a Ctrl+H az elozmenyeket nyitna meg; hivas kozben ezt
   * elnyeljuk. Hivason kivul egyikhez sem nyulunk.
   *
   * Szunetben a Ctrl+M nem csinal semmit: ott a mikrofont a szerver is lezarta,
   * tehat a "feloldas" hazudna (lasd a vezerlosav gombjait).
   */
  const shortcutsRef = useRef({ toggleHand, toggleMute });
  shortcutsRef.current = { toggleHand, toggleMute };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.ctrlKey || event.altKey || event.shiftKey || event.metaKey) return;
      const key = event.key.toLowerCase();
      if (key !== 'h' && key !== 'm') return;
      if (!isLiveState(stateRef.current)) return;

      event.preventDefault();
      if (event.repeat) return;
      if (key === 'h') shortcutsRef.current.toggleHand();
      else if (!pausedRef.current) shortcutsRef.current.toggleMute();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const sendStroke = useCallback(
    (stroke: Omit<Stroke, 'from'>) => {
      const from = roomRef.current?.localParticipant.identity ?? '';
      const frame = { type: 'draw' as const, ...stroke, from };
      publishData(frame);
      setStrokes((current) => [...current.slice(-300), frame]);
    },
    [publishData],
  );

  const clearStrokes = useCallback(
    (target: string) => {
      const from = roomRef.current?.localParticipant.identity ?? '';
      publishData({ type: 'draw.clear', target, from });
      setStrokes((current) => current.filter((stroke) => stroke.target !== target));
    },
    [publishData],
  );

  /**
   * Az orszem kikapcsolta a hatteret, mert a gep nem birta (lasd `huddle/blur.ts`).
   * A kamera megy tovabb; a valaszto mutassa az igazat, es mondjuk meg, miert.
   */
  const onBackgroundTooSlow = useCallback(() => {
    setBackgroundState(NO_BACKGROUND);
    setError('A háttér túl lassú ezen a gépen, ezért kikapcsoltuk – a kamera megy tovább.');
  }, []);

  const toggleCamera = useCallback(async () => {
    const room = roomRef.current;
    /*
      EGYSZERRE EGY valtas.

      Gyors egymas utani kattintasnal a masodik meg a regi `isCameraOn`-bol
      szamolt, es egy bekapcsolas meg egy kikapcsolas futott egymasba - a kep
      fekete maradt, es tobbe nem jott vissza. Amig egy valtas tart, a gomb
      nem fogad ujabbat (es ezt latni is rajta).
    */
    if (!room || cameraBusyRef.current) return;
    cameraBusyRef.current = true;
    setIsCameraBusy(true);

    const next = !isCameraOn;
    try {
      /**
       * A kamera nem mindig mond nemet - van, hogy egyszeruen nem valaszol.
       *
       * Foglalt eszkoz, rossz meghajto vagy vegtelenul varo vedelmi szoftver
       * eseten a `setCameraEnabled` sosem ter vissza. Idokorlat nelkul a gomb
       * csendben kikapcsolva maradna, es a felhasznalo nem tudna, mi tortenik
       * - pontosan az a "nem jelez" viselkedes, ami miatt ez a projekt elindult.
       */
      await Promise.race([
        room.localParticipant.setCameraEnabled(next),
        new Promise((_resolve, reject) =>
          setTimeout(() => reject(new Error('a kamera nem válaszolt')), CAMERA_TIMEOUT_MS),
        ),
      ]);
      setIsCameraOn(next);
      // A csempe a sav nemitasat koveti (lasd refreshCameraTiles) - nem vesszuk ki.
      refreshCameraTiles();

      /*
        Bekapcsolaskor a megjegyzett hatter azonnal ervenybe lep.

        Enelkul minden hivasban ujra be kellene kapcsolni - es aki azert
        hasznalja, mert a hattereben mas is dolgozik, annak eppen az elso
        masodpercek szamitanak.
      */
      if (next && background.kind !== 'none') {
        const track = localCameraTrack(room);
        if (track) {
          setBackgroundState(await applyBackground(track, background, onBackgroundTooSlow));
        }
      }

      /*
        Bekapcsolas utan UJRA kerjuk az eszkozlistat.

        A bongeszo a kamerak nevet csak KAMERA-engedely mellett arulja el (a
        mikrofone nem eleg hozza). Az elso bekapcsolasig ezert ures a
        kamera-valaszto - utana viszont azonnal legyen benne minden.
      */
      if (next) {
        void refreshDevices();
        setActiveCameraId(room.getActiveDevice('videoinput') ?? null);
      }

      void sendHeartbeat({ isCameraOn: next });
    } catch (err) {
      // A felszabaditas fontos: kulonben a felig megnyitott eszkoz bent ragad.
      void room.localParticipant.setCameraEnabled(false).catch(() => undefined);
      setIsCameraOn(false);
      refreshCameraTiles();
      setError(
        err instanceof Error ? `A kamera nem indult el: ${err.message}` : 'A kamera nem indult el.',
      );
    } finally {
      cameraBusyRef.current = false;
      setIsCameraBusy(false);
    }
  }, [
    isCameraOn,
    background,
    refreshCameraTiles,
    refreshDevices,
    sendHeartbeat,
    onBackgroundTooSlow,
  ]);

  /**
   * Hatter valasztasa: semmi, elmosas, vagy egy sajat kep.
   *
   * Csak elo kamerakepen ertelmes. A beallitast viszont KIKAPCSOLT kameraval is
   * meg lehet valtoztatni - akkor a kovetkezo bekapcsolasnal lep ervenybe.
   */
  const setBackground = useCallback(
    async (next: VideoBackground) => {
      rememberBackground(next);

      const room = roomRef.current;
      const track = room ? localCameraTrack(room) : null;
      if (!track) {
        // Nincs elo kamera: csak a beallitast jegyezzuk meg.
        setBackgroundState(next);
        return;
      }

      /*
      Ha a szuro nem indul el (regi gep, hianyzo WASM-tamogatas, torolt kep),
      az `applyBackground` "semmit" ad - es a valaszto NEM mutat hazugul egy
      hatteret. A kamera kepe hatter nelkul megy tovabb, nem all le.
    */
      const applied = await applyBackground(track, next, onBackgroundTooSlow);
      setBackgroundState(applied);
      if (applied.kind !== next.kind) setError('A háttér nem kapcsolható be ezen a gépen.');
    },
    [onBackgroundTooSlow],
  );

  /**
   * Zajszures szintjenek valtasa. Hivas kozben azonnal ervenybe lep; hivason
   * kivul csak megjegyezzuk.
   */
  const setNoiseLevel = useCallback(async (next: NoiseLevel) => {
    rememberNoise(next);
    noiseWantedRef.current = next;

    // A telefonos hivo hangja ugyanazt a beallitast koveti.
    const room = roomRef.current;
    if (next === 'off') {
      for (const filter of phoneFiltersRef.current.values()) filter.stop();
      phoneFiltersRef.current.clear();
    } else if (room) {
      for (const participant of room.remoteParticipants.values()) {
        if (!participant.identity.startsWith('sip_')) continue;
        for (const publication of participant.audioTrackPublications.values()) {
          const track = publication.track;
          if (!(track instanceof lk().RemoteAudioTrack)) continue;
          const sid = track.sid ?? participant.identity;
          const running = phoneFiltersRef.current.get(sid);
          if (running) running.setLevel(next);
          else {
            void applyRemoteNoiseFilter(track, next).then((filter) => {
              if (filter) phoneFiltersRef.current.set(sid, filter);
            });
          }
        }
      }
    }

    const track = room ? localMicrophoneAudioTrack(room) : null;
    if (!track) {
      setNoiseLevelState(next);
      return;
    }

    const applied = await applyNoiseFilter(track, next);
    setNoiseLevelState(applied);
    if (applied !== next) {
      setError(
        'A zajszűrés nem kapcsolható be ezen a gépen - a böngésző saját szűrése marad bekapcsolva.',
      );
    }
  }, []);

  /**
   * Masik resztvevo elnemitasa.
   *
   * A visszakapcsolast CSAK az erintett vegezheti - a sajat mikrofonjahoz
   * mindenki maga fer hozza.
   */
  /**
   * Egy resztvevo hangereje - CSAK NALUNK.
   *
   * Nem tevesztendo ossze a `muteOther`-rel: az a szerverre megy es MINDENKINEL
   * elnemiti az illetot; ez csak a sajat lejatszasunkat allitja, a masik fel
   * es a tobbiek semmit nem vesznek eszre belole.
   *
   * Szuneten NEM allitjuk ra azonnal a savra: ott mindenki nema, es a
   * visszateres ugyis ebbol az allapotbol olvas.
   */
  const setParticipantVolume = useCallback((userId: string, volume: number) => {
    const value = clamp(volume);

    setParticipantVolumes((current) => {
      const next = { ...current };
      if (value === 1) delete next[userId];
      else next[userId] = value;
      // A ref AZONNAL kovesse: a hang rakotese ebbol olvas, es az megelozheti
      // a kovetkezo ujrarajzolast.
      volumesRef.current = next;
      return next;
    });

    rememberVolume(userId, value);

    if (pausedRef.current) return;
    const participant = roomRef.current?.remoteParticipants.get(userId);
    participant?.setVolume(value);
  }, []);

  const muteOther = useCallback(async (userId: string) => {
    const current = huddleRef.current;
    if (!current) return;
    await muteHuddleParticipant(current.id, userId).catch(() => undefined);
  }, []);

  const invite = useCallback(
    async (userIds: string[]) => {
      const current = huddleRef.current;
      if (!current) return;

      /**
       * A valasz a friss resztvevo-listat is hozza, es azt AZONNAL kirakjuk.
       *
       * Enelkul a "hivjuk" csempe csak a kovetkezo eletjelnel (20 mp) jelenne
       * meg - a hivo addig nem latna, hogy elment-e a hivas egyaltalan.
       */
      const result = await inviteToHuddle(current.id, userIds).catch(() => null);
      if (!result || huddleRef.current?.id !== current.id) return;

      setHuddle(result.huddle);
      upsertHuddle(queryClient, result.huddle);
    },
    [queryClient],
  );

  /**
   * A behivas VISSZAVONASA: a hivott meg nem vette fel.
   *
   * A valasz a friss resztvevo-listat hozza, es azonnal kirakjuk: a `hivjuk` csempe
   * ne a kovetkezo eletjelnel (20 mp) tunjon el. Hiba eseten (pl. kozben belepett) nem
   * hallgatunk el semmit - egy visszavonas, ami latszolag nem tortenik meg, rosszabb,
   * mint egy kimondott hiba.
   */
  const cancelInvite = useCallback(
    async (userId: string) => {
      const current = huddleRef.current;
      if (!current) return;
      try {
        const result = await cancelHuddleInvite(current.id, userId);
        if (huddleRef.current?.id !== current.id) return;
        setHuddle(result.huddle);
        upsertHuddle(queryClient, result.huddle);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'A behívás visszavonása nem sikerült.');
      }
    },
    [queryClient],
  );

  /*
    ZOLD KERET a megosztott kijelzo szelen - es a kijelzo-valasztas.

    A megosztas (ujra)inditasa utan fut: az elso inditaskor es a megosztott
    tartalom MEGOSZTAS KOZBENI cserejekor is, mert a csere mas kijelzore is
    vihet. Nem a hivas ablakaban rajzol - ott eppen NEM latszik, ha a megosztott
    monitor a masik. A rajzolast az asztali app vegzi; bongeszoben nem lehetseges,
    es ezt a felulet ki is mondja.
  */
  const updateShareFrame = useCallback(async (room: Room) => {
    const track = localScreenTrack(room);
    let result: ShareFrameState = track ? await showShareFrame(track) : 'ask';
    let monitors: Monitor[] = [];

    /*
      Ha a meret nem dontott, MEGKERDEZZUK MAGAT A KEPET.

      Minden szoba joheto kijelzo bal felso sarkaba kerul egy apro, mas-mas
      szinu potty, es a megosztott kepbol kiolvassuk, melyik latszik. Ez
      meres, nem talalgatas - ezert lehet kerdes nelkul is helyes.

      Bizonytalan eredmenynel marad a kerdes: a rossz kijelzore tett keret
      hamis biztonsagot adna arrol, mit lat a masik fel.
    */
    if (result === 'ask' && track) {
      monitors = await listMonitors();

      // Csak azok johetnek szoba, amiknek a merete illik a rogzitett kepre.
      const size = sourceSize(track);
      const candidates = size
        ? monitors.filter((m) => m.width === size.width && m.height === size.height)
        : monitors;

      const found = await probeMonitor(track, candidates.length > 0 ? candidates : monitors);
      if (found) result = await pickShareMonitor(track, found);
    }

    setShareFrame(result);
    // Csak akkor kerdezunk, ha a meres sem dontott.
    setShareMonitors(result === 'ask' ? monitors : []);
  }, []);
  const toggleScreenShare = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;

    const next = !isSharingScreen;
    try {
      await room.localParticipant.setScreenShareEnabled(next);
      setIsSharingScreen(next);
      if (!next) {
        removeLocalTile(room, setScreenShares);
        // Csak a SAJAT kepunket vesszuk le - amit masoktol nezunk, az marad.
        setVisibleShares((current) =>
          current.filter((id) => id !== room.localParticipant.identity),
        );
      }

      /*
        ZOLD KERET a megosztott kijelzo szelen.

        Nem a hivas ablakaban - ott ugyanis eppen NEM latszik, ha a megosztott
        monitor a masik. A rajzolast az asztali app vegzi; bongeszoben nem
        lehetseges, es ezt a felulet ki is mondja.
      */
      if (next) {
        await updateShareFrame(room);
      } else {
        setShareFrame('off');
        setShareMonitors([]);
        void hideShareFrame();
      }

      void sendHeartbeat({ isSharingScreen: next });
    } catch (err) {
      // A felhasznalo megsem valasztott ablakot - ez nem hiba, csak dontes.
      if (err instanceof DOMException && err.name === 'NotAllowedError') return;
      setShareFrame('off');
      setShareMonitors([]);
      void hideShareFrame();
      setError(
        err instanceof Error
          ? describeMediaError(err, 'screen')
          : 'A képernyőmegosztás nem indult el.',
      );
    }
  }, [isSharingScreen, sendHeartbeat, updateShareFrame]);

  /**
   * A MEGOSZTOTT TARTALOM CSERELESE megosztas kozben.
   *
   * Eddig masik ablakot csak ugy lehetett megosztani, hogy a felhasznalo lekapcsolta
   * a megosztast, aztan ujra bekapcsolta - a tobbiek kozben megszakadt kepet lattak, es
   * a hivas ablaka is atrendezodott. Most az UJ forrast ELOSZOR kerjuk meg
   * (a bongeszo ablakvalasztoja, felhasznaloi kattintasra), es csak ha ez sikerult,
   * cseréljuk a kuldott savot a LiveKit `replaceTrack`-jevel: a megosztas ugyanaz a
   * publikacio marad. Ha a felhasznalo megsem valaszt, a regi megosztas valtozatlanul megy.
   */
  const switchScreenShare = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const current = room.localParticipant.getTrackPublication(lk().Track.Source.ScreenShare)?.track;
    if (!current) return;

    try {
      const created = await lk().createLocalScreenTracks({ audio: false });
      const video = created.find((track) => track.kind === lk().Track.Kind.Video);
      // Ami nem kell (pl. egy hangsav), azt leallitjuk: ne maradjon felvevo folyamat.
      created.filter((track) => track !== video).forEach((track) => track.stop());
      if (!video) return;

      // `false`: nem a felhasznalo adta a savot, ezert a LiveKit a RÉGIT le is allitja.
      await current.replaceTrack(video.mediaStreamTrack, false);
      await updateShareFrame(room);
    } catch (err) {
      // A felhasznalo megsem valasztott ablakot: a regi megosztas marad, ez nem hiba.
      if (err instanceof DOMException && err.name === 'NotAllowedError') return;
      setError(
        err instanceof Error
          ? `A megosztott tartalom cseréje nem sikerült: ${err.message}`
          : 'A megosztott tartalom cseréje nem sikerült.',
      );
    }
  }, [updateShareFrame]);

  /**
   * Valasz a bejovo keresre.
   *
   * Az "Igen" KATTINTAS maga inditja a megosztast: a bongeszo csak felhasznaloi
   * mozdulatra nyitja meg az ablakvalasztot, ezert nem lehet a kerestol
   * magatol elinditani. Ha a valasztot bezarja, az a kero szemszogebol
   * elutasitas.
   */
  const answerRequest = useCallback(
    (accept: boolean) => {
      const request = incomingRequest;
      if (!request) return;
      if (incomingTimerRef.current !== null) window.clearTimeout(incomingTimerRef.current);
      setIncomingRequest(null);

      if (!accept) {
        sendAnswer(request.from, request.id, 'declined');
        return;
      }

      if (request.kind === 'unmute') {
        if (!(roomRef.current?.localParticipant.isMicrophoneEnabled ?? true)) toggleMute();
        sendAnswer(request.from, request.id, 'accepted');
        return;
      }

      if (roomRef.current?.localParticipant.isScreenShareEnabled ?? false) {
        sendAnswer(request.from, request.id, 'accepted');
        return;
      }
      void toggleScreenShare().then(() =>
        sendAnswer(
          request.from,
          request.id,
          (roomRef.current?.localParticipant.isScreenShareEnabled ?? false)
            ? 'accepted'
            : 'declined',
        ),
      );
    },
    [incomingRequest, sendAnswer, toggleMute, toggleScreenShare],
  );

  /**
   * Szunet: bent maradok, de eppen nem vagyok itt.
   *
   * Erre azert van szukseg, mert egy nap tobb megbeszelesbol all: kozben jon
   * egy Teams-hivas, vagy epp egy masik olelest kell inditani. A "kilepek,
   * aztan majd visszajovok" ilyenkor rossz valasz - elveszik a hivas, es a
   * tobbiek szamara ugy nez ki, mintha otthagytuk volna oket.
   *
   * A szunet HAROM dolgot tesz egyszerre:
   *  1. **Elnemit mindket iranyban.** Nem szolunk bele, es nem is halljuk.
   *     A felfele iranyt a szerver is elvegzi (lasd a heartbeat vegpontot):
   *     egy mashol jaro ember mikrofonja nem maradhat nyitva.
   *  2. **Kikapcsolja a kamerat es a megosztast.** Aki szuneten van, annak a
   *     kepe nem megy tovabb - ez nem kenyelmi kerdes.
   *  3. **Elengedi az "elo hivas" igenyt.** Ettol lehet kozben egy MASIK
   *     olelesbe belepni: az uj ablak igenybejelenteset ez az ablak mar nem
   *     veszi magara. Visszateresnel viszont VISSZAVESSZUK az igenyt - vagyis
   *     a masik hivas zar be, nem lesz ket elo mikrofon.
   *
   * A tobbiek mindebbol egy dolgot latnak: hogy szuneten vagyunk. Ez a lenyeg
   * - a "miert nem valaszol" es a "kilepett" ket kulonbozo dolog.
   */
  /**
   * A felhasznalo megmondta, melyik kijelzot osztja meg.
   *
   * A valasztast az asztali app jegyzi meg (gepenkent), tehat legkozelebb mar
   * nem kerdezunk - csak akkor, ha az a monitor kozben eltunt.
   */
  const chooseShareMonitor = useCallback(async (name: string) => {
    const room = roomRef.current;
    const track = room ? localScreenTrack(room) : null;
    if (!track) return;

    const result = await pickShareMonitor(track, name);
    setShareFrame(result);
    if (result === 'shown') setShareMonitors([]);
  }, []);

  const togglePause = useCallback(async () => {
    const room = roomRef.current;
    const current = huddleRef.current;
    if (!room || !current) return;

    const next = !pausedRef.current;

    // ELOSZOR a ref: a hang rakotese es a masik ablak igenybejelentese ebbol
    // olvas, es mindketto megelozheti a kovetkezo ujrarajzolast.
    pausedRef.current = next;
    setIsPaused(next);

    /*
      A hang mindket iranyban - ez a szunet lenyege, ezert ez megy elsokent.

      HANGEROVEL csendesitunk, nem az elem nemitasaval: a tavoli hang Web Audio
      uton szol, ott az elem `muted` jelzoje mar nem szamit. A `setVolume`
      mindket uton mukodik.
    */
    for (const participant of room.remoteParticipants.values()) {
      /*
        Visszaterve a SAJAT beallitasunkra allunk vissza, nem fix egyre.

        Fix `1`-gyel a szunet csendben torolne a szemelyenkenti hangerot: aki
        valakit 30%-ra halkitott, majd szunetre ment es visszajott, azt ujra
        teljes hangeron hallana - es nem ertene, miert.
      */
      participant.setVolume(next ? 0 : (volumesRef.current[participant.identity] ?? 1));
    }

    if (next) {
      await room.localParticipant.setMicrophoneEnabled(false).catch(() => undefined);
      setIsMuted(true);

      if (isCameraOn) {
        await room.localParticipant.setCameraEnabled(false).catch(() => undefined);
        setIsCameraOn(false);
        refreshCameraTiles();
      }
      if (isSharingScreen) {
        await room.localParticipant.setScreenShareEnabled(false).catch(() => undefined);
        setIsSharingScreen(false);
        removeLocalTile(room, setScreenShares);
        setVisibleShares((current) =>
          current.filter((id) => id !== room.localParticipant.identity),
        );
      }
    } else {
      /*
        Visszaterunk: ez lesz UJRA az elo hivas.

        Az igenybejelentes bezarja a tobbi (nem szuneteltetett) hivas-ablakot -
        ugyanaz a szabaly, mint a csatlakozasnal. Nemitva terunk vissza, mert
        a mikrofont a szerver is lezarta: a visszakapcsolas tudatos mozdulat.
      */
      broadcastRef.current?.postMessage({
        type: 'claim',
        sessionId: SESSION_ID,
        huddleId: current.id,
      } satisfies BroadcastClaim);
    }

    await sendHeartbeat({ isPaused: next, isMuted: true, isCameraOn: false });
  }, [isCameraOn, isSharingScreen, refreshCameraTiles, sendHeartbeat]);
  togglePauseRef.current = togglePause;

  /**
   * A hang bekapcsolasa felhasznaloi mozdulatra.
   *
   * A bongeszo eppen ezt varja: egy kattintast. Ezert nem probalkozunk
   * magunktol ujra es ujra - egy gombot teszunk oda, ami egyszer megoldja.
   */
  const enableAudio = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;

    try {
      await room.startAudio();
      setIsAudioBlocked(!room.canPlaybackAudio);
    } catch (err) {
      setError(
        err instanceof Error
          ? `A hang bekapcsolása nem sikerült: ${err.message}`
          : 'A hang bekapcsolása nem sikerült.',
      );
    }
  }, []);

  const switchOutput = useCallback(
    async (deviceId: string) => {
      // ELOSZOR a ref: a rakotes ebbol olvas, es megelozheti az ujrarajzolast.
      outputIdRef.current = deviceId;
      setActiveOutputId(deviceId);
      remember('audiooutput', deviceId, outputDevices);

      try {
        /*
          A valtast a LiveKit vegzi: Web Audio uton az `AudioContext`
          kimenetet allitja at, amit a Chrome tiszteletben tart.
        */
        await roomRef.current?.switchActiveDevice('audiooutput', deviceId);

        // Halonak, elemszinten is - lasd `applyOutput`.
        await applyOutput(deviceId).catch(() => undefined);
      } catch (err) {
        outputIdRef.current = null;
        setActiveOutputId(null);
        setError(
          err instanceof Error
            ? `A hangszóró váltása nem sikerült: ${err.message}`
            : 'A hangszóró váltása nem sikerült.',
        );
      }

      /*
        SIKERT NEM JELENTUNK.

        Az elozo valtozat "atallitva (1/1 hangforras)"-t irt ki, visszaolvasott
        `sinkId`-vel - es kozben a hang a regi eszkozon szolt tovabb. Egy
        magabiztos, de hamis visszajelzes rosszabb a semminel: a felhasznalot
        elviszi a hiba valodi helyerol. Ha nem tudjuk bizonyitani, hallgatunk;
        a valasztott eszkoz megjelolese amugy is latszik a listaban.
      */
    },
    [applyOutput, outputDevices],
  );

  const switchDevice = useCallback(
    async (deviceId: string) => {
      const room = roomRef.current;
      if (!room) return;
      try {
        // A LiveKit false-ot ad, ha az eszkozt nem sikerult megnyitni - ez NEM kivetel.
        const switched = await room.switchActiveDevice('audioinput', deviceId);
        if (!switched) {
          setError(
            'A mikrofon váltása nem sikerült: az eszköz nem nyitható meg (másik program használhatja, vagy kihúzták).',
          );
          return;
        }
        setActiveDeviceId(deviceId);
        remember('audioinput', deviceId, devices);
      } catch (err) {
        setError(
          err instanceof Error
            ? describeMediaError(err, 'microphone')
            : 'Az eszközváltás nem sikerült.',
        );
      }
    },
    [devices],
  );

  /**
   * Kameravaltas.
   *
   * A LiveKit KIKAPCSOLT kameraval is elfogadja: ilyenkor csak feljegyzi, es a
   * kovetkezo bekapcsolasnal ezt az eszkozt nyitja meg. Ezert lehet a
   * kamerat elore kivalasztani, es nem kell hozza adasba lepni.
   */
  const switchCamera = useCallback(
    async (deviceId: string) => {
      const room = roomRef.current;
      if (!room) return;
      try {
        const switched = await room.switchActiveDevice('videoinput', deviceId);
        if (!switched) {
          setError('A kamera váltása nem sikerült: az eszköz nem nyitható meg.');
          return;
        }
        setActiveCameraId(deviceId);
        remember('videoinput', deviceId, videoDevices);
      } catch (err) {
        setError(
          err instanceof Error
            ? describeMediaError(err, 'camera')
            : 'A kamera váltása nem sikerült.',
        );
      }
    },
    [videoDevices],
  );

  // A ful bezarasakor is lepjunk ki rendesen, hogy ne maradjon szellem.
  useEffect(() => {
    const onUnload = () => {
      // Csatlakozas kozben zartak be: ez megszakitas, nem kudarc.
      const pending = pendingJoinRef.current;
      if (pending?.huddleId && !pending.outcome) {
        pending.outcome = 'join_cancelled';
        navigator.sendBeacon?.(
          `/api/huddles/${pending.huddleId}/events`,
          new Blob(
            [
              JSON.stringify({
                kind: 'join_cancelled',
                detail: 'Az ablakot bezárták csatlakozás közben',
              }),
            ],
            { type: 'application/json' },
          ),
        );
      }
      const current = huddleRef.current;
      if (!current) return;
      // A `sendBeacon` a lap bezarasa kozben is atmegy.
      navigator.sendBeacon?.(
        `/api/huddles/${current.id}/leave`,
        new Blob([JSON.stringify({ sessionId: SESSION_ID })], { type: 'application/json' }),
      );
    };

    window.addEventListener('pagehide', onUnload);
    return () => window.removeEventListener('pagehide', onUnload);
  }, []);

  useEffect(
    () => () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      void roomRef.current?.disconnect().catch(() => undefined);
    },
    [],
  );

  // ─── Atirat: a sajat mikrofon felvetele ─────────────────────────────────────

  const huddleId = huddle?.id ?? null;
  const isTranscribing = huddle?.transcribe ?? false;
  const huddleStartedAt = huddle?.startedAt ?? null;

  /**
   * A felvetel PONTOSAN akkor megy, amikor beszelunk.
   *
   * Negy feltetel egyszerre: elo a hivas, be van kapcsolva az atiratozas, a
   * mikrofonunk nyitva van, es nem vagyunk szuneten. Ha barmelyik megszunik, a
   * felvetel leall - es a mar felvett darab meg felmegy.
   *
   * A nemitas azert szamit, mert nemitva a sav CSENDET ad: azt feltolteni
   * savszelesseg-pazarlas, a felismerok pedig a csendbe hajlamosak
   * belekepzelni mondatokat. Igy egy tiz fos megbeszelesen egyszerre altalaban
   * egy valakinek megy a felvetele - nem tiznek.
   */
  useEffect(() => {
    const room = roomRef.current;
    // Kiscsoportos idoszak alatt az atirat SZUNETEL (a szerver is elejti az atfedo darabokat).
    const shouldRecord =
      isLiveState(state) &&
      isTranscribing &&
      !isMuted &&
      !isPaused &&
      !breakoutActive &&
      Boolean(room && huddleId);

    if (!shouldRecord) {
      if (recorderRef.current) {
        recorderRef.current.stop();
        recorderRef.current = null;
        setIsRecording(false);
      }
      return;
    }

    if (recorderRef.current) return;

    /*
      A SAV MEG NEM LETEZIK - ez NEM vegleges.

      Ez egy valodi, sokaig eszrevetlen hiba volt: a nemitas feloldasakor az
      `isMuted` allapot AZONNAL atvalt, a mikrofonsav viszont csak utana jon
      letre (a `setMicrophoneEnabled` aszinkron). A hatas ilyenkor lefutott,
      nem talalt savot, es csendben feladta - a fuggosegei kozott pedig semmi
      nem valtozott tobbe, tehat SOSEM probalta ujra.

      Kovetkezmenye: akinel a sav lassabban jott letre, annak egyetlen szava
      sem kerult az atiratba - mikozben a hivas maga tokeletesen mukodott. Egy
      resztvevo mondatai egyszeruen hianyoztak, es semmi nem szolt rola.

      A `micGeneration` a megoldas: a sav letrejottekor no, es ez a hatast ujra
      lefuttatja - most mar a meglevo savval.
    */
    const micTrack = localMicrophoneTrack(room!);
    if (!micTrack) return;

    if (!pickMimeType()) {
      // Egyszer, halkan: a hivas ettol meg megy tovabb.
      setError('Ez a böngésző nem tud hangot rögzíteni, így nem készül átirat.');
      return;
    }

    uploaderRef.current ??= new ChunkUploader(huddleId!, setError);
    const uploader = uploaderRef.current;

    /*
      Az idozites a hivas kezdetehez igazodik.

      A sajat oránk es a szerver oraja kozotti elteres ennyivel tolja el ennek
      a resztvevonek a szakaszait. NTP mellett ez masodperc alatti, es a
      resztvevon BELUL a sorrendet egyaltalan nem bantja.
    */
    const startedAtMs = huddleStartedAt ? Date.parse(huddleStartedAt) : Date.now();

    /*
      A FELVETEL SAJAT SAVOT NYIT - ez aszinkron, tehat megszakithato.

      A masodik `getUserMedia` eltarthat par tized masodpercig, es kozben a
      hatas fusthatja is (nemitas, hivas vege). A `megszakadt` orzi, hogy egy
      kozben elavult inditas ne hagyjon maga utan futo felvetelt es nyitva
      hagyott mikrofont.
    */
    let megszakadt = false;
    let sajatSav: MediaStream | null = null;
    let recorder: ChunkRecorder | null = null;

    void (async () => {
      sajatSav = await openTranscriptStream(micTrack);
      if (megszakadt) {
        closeTranscriptStream(sajatSav);
        return;
      }

      /*
        Ha a sajat sav nem johetett letre, a HIVAS savjaval veszunk fel.

        Gyengebb atirat, de atirat: a funkcio nem all le azert, mert a jobbik
        ut nem jarhato ezen az eszkozon.
      */
      const stream = sajatSav ?? new MediaStream([micTrack]);

      recorder = new ChunkRecorder({
        stream,
        offsetMs: () => Math.max(0, Date.now() - startedAtMs),
        onChunk: (chunk) => uploader.add(chunk),
        onError: setError,
      });

      if (recorder.start()) {
        recorderRef.current = recorder;
        setIsRecording(true);
      } else {
        closeTranscriptStream(sajatSav);
        sajatSav = null;
      }
    })();

    return () => {
      megszakadt = true;
      recorder?.stop();
      closeTranscriptStream(sajatSav);
      if (recorder && recorderRef.current === recorder) recorderRef.current = null;
      setIsRecording(false);
    };
  }, [
    state,
    isTranscribing,
    isMuted,
    isPaused,
    breakoutActive,
    huddleId,
    huddleStartedAt,
    micGeneration,
  ]);

  /**
   * A TELEFONOS FEL felvetele az atirathoz.
   *
   * A hivonak nincs bongeszoje, ami a sajat hangjat felvenne - a hangja a
   * LiveKit-szobaban el, es minden bent ulo kolléga megkapja. EGYIKUNK veszi
   * fel: a legregebben bent levo. Ez mindenkinel ugyanaz a dontes (a huddle
   * allapotabol szamoljuk), tehat nem keszul ket peldany ugyanarrol.
   *
   * ATADASNAL a felvetel magatol atkerul: amikor az atado kilep, az atvevo
   * lesz a legregebbi, es nala indul. A darabok a hivas idovonalan vannak,
   * igy az atirat folytonos marad.
   *
   * A sajat nemitasunk, szunetunk nem szamit: a hivo hangja attol meg megy.
   */
  const phoneLegs = huddle?.phoneLegs;
  const participantsForRecorder = huddle?.participants;
  const phoneRecording = (() => {
    const room = roomRef.current;
    if (!room || !isLiveState(state) || !isTranscribing || breakoutActive) return null;
    const leg = phoneLegs?.find((item) => item.state === 'active');
    if (!leg) return null;

    const inside = (participantsForRecorder ?? [])
      .filter((p) => p.state === 'joined')
      .sort((a, b) => a.joinedAt.localeCompare(b.joinedAt) || a.user.id.localeCompare(b.user.id));
    if (inside[0]?.user.id !== room.localParticipant.identity) return null;
    return { legId: leg.id, identity: leg.identity };
  })();
  const phoneLegToRecord = phoneRecording?.legId ?? null;
  const phoneIdentityToRecord = phoneRecording?.identity ?? null;

  useEffect(() => {
    const room = roomRef.current;
    if (!room || !huddleId || !phoneLegToRecord || !phoneIdentityToRecord) return;

    const caller = room.remoteParticipants.get(phoneIdentityToRecord);
    const track = caller
      ? [...caller.audioTrackPublications.values()].find((pub) => pub.track)?.track
      : undefined;
    // A sav meg nem jott meg: a `phoneTrackGeneration` ujrafuttat, amint megjon.
    if (!track?.mediaStreamTrack) return;
    if (!pickMimeType()) return;

    const startedAtMs = huddleStartedAt ? Date.parse(huddleStartedAt) : Date.now();
    const uploader = new ChunkUploader(huddleId, setError, phoneLegToRecord);
    const recorder = new ChunkRecorder({
      stream: new MediaStream([track.mediaStreamTrack]),
      offsetMs: () => Math.max(0, Date.now() - startedAtMs),
      onChunk: (chunk) => uploader.add(chunk),
      onError: setError,
    });
    if (!recorder.start()) return;

    return () => {
      // A mar felvett darab meg felmegy: a stop kiadja az utolsot.
      recorder.stop();
    };
  }, [huddleId, huddleStartedAt, phoneLegToRecord, phoneIdentityToRecord, phoneTrackGeneration]);

  /**
   * Az atiratozas be-/kikapcsolasa.
   *
   * A valtozas a huddle allapotaval megy ki mindenkinek: aki bent ul, azonnal
   * latja, hogy felvetel indult vagy allt le.
   */
  const toggleTranscribe = useCallback(async () => {
    const current = huddleRef.current;
    if (!current) return;

    try {
      await setHuddleTranscribe(current.id, !current.transcribe);
      // A friss allapotot a szervertol kerjuk vissza, nem magunknak talaljuk ki.
      await sendHeartbeat();
    } catch (err) {
      setError(
        err instanceof ApiRequestError ? err.message : 'Az átiratozás átállítása nem sikerült.',
      );
    }
  }, [sendHeartbeat]);

  const value = useMemo<HuddleContextValue>(
    () => ({
      state,
      huddle,
      breakout,
      breakoutEndsAtMs,
      myBreakoutGroup,
      helpRequests,
      requestBreakoutHelp,
      dismissHelp,
      channelId,
      error,
      preflight,
      speakingIds,
      isMuted,
      isCameraOn,
      isCameraBusy,
      isPaused,
      background,
      noiseLevel,
      isSharingScreen,
      screenShares,
      visibleShares,
      shareFrame,
      shareMonitors,
      chooseShareMonitor,
      cameraTracks,
      reactions,
      raisedHands,
      toggleHand,
      quickVotes,
      castQuickVote,
      clearQuickVotes,
      awayIds,
      toggleAway,
      strokes,
      devices,
      activeDeviceId,
      outputDevices,
      activeOutputId,
      videoDevices,
      activeCameraId,
      canChooseOutput: canChooseOutput(),
      isAudioBlocked,
      enableAudio,
      join,
      leave,
      toggleMute,
      togglePause,
      toggleCamera,
      setBackground,
      setNoiseLevel,
      toggleScreenShare,
      switchScreenShare,
      switchDevice,
      switchOutput,
      switchCamera,
      toggleShare,
      sendReaction,
      sendStroke,
      clearStrokes,
      muteOther,
      participantVolumes,
      setParticipantVolume,
      invite,
      cancelInvite,
      requestFrom,
      incomingRequest,
      answerRequest,
      requestNotice,
      dismissRequestNotice: () => setRequestNotice(null),
      chatSignal,
      notifyChat,
      isTranscribing,
      isRecording,
      toggleTranscribe,
      dismissError: () => {
        setError(null);
        // A `failed` csak addig tart, amig a hibauzenet lathato.
        if (stateRef.current === 'failed') transition('idle');
      },
    }),
    [
      state,
      huddle,
      breakout,
      breakoutEndsAtMs,
      myBreakoutGroup,
      helpRequests,
      requestBreakoutHelp,
      dismissHelp,
      channelId,
      error,
      preflight,
      speakingIds,
      isMuted,
      isCameraOn,
      isCameraBusy,
      isPaused,
      background,
      noiseLevel,
      isSharingScreen,
      screenShares,
      visibleShares,
      shareFrame,
      shareMonitors,
      chooseShareMonitor,
      cameraTracks,
      reactions,
      raisedHands,
      toggleHand,
      quickVotes,
      castQuickVote,
      clearQuickVotes,
      awayIds,
      toggleAway,
      strokes,
      devices,
      activeDeviceId,
      outputDevices,
      activeOutputId,
      videoDevices,
      activeCameraId,
      isAudioBlocked,
      enableAudio,
      join,
      leave,
      toggleMute,
      togglePause,
      toggleCamera,
      setBackground,
      setNoiseLevel,
      toggleScreenShare,
      switchScreenShare,
      switchDevice,
      switchOutput,
      switchCamera,
      sendReaction,
      sendStroke,
      clearStrokes,
      muteOther,
      participantVolumes,
      setParticipantVolume,
      invite,
      cancelInvite,
      requestFrom,
      incomingRequest,
      answerRequest,
      requestNotice,
      chatSignal,
      notifyChat,
      isTranscribing,
      isRecording,
      toggleTranscribe,
      toggleShare,
      transition,
    ],
  );

  return (
    <HuddleContext.Provider value={value}>
      {children}
      {/* A tavoli hangok. Rejtett, de a DOM-ban kell lennie. */}
      <div ref={audioRef} className="sr-only" aria-hidden />
    </HuddleContext.Provider>
  );
}

export function useHuddle(): HuddleContextValue {
  const value = useContext(HuddleContext);
  if (!value) throw new Error('A useHuddle csak HuddleProvider alatt használható.');
  return value;
}
